import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatRate } from '../constants/rateFormat';
import MessageAttachment from '../components/MessageAttachment';
import RichMessageBody, { hasRichBody } from '../components/RichMessageBody';
import StaffComposer from '../components/admin/StaffComposer';
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const BLUE = '#3a6ad9';

const PIPELINE_STAGES = [
  { key: 'new', label: 'New' },
  { key: 'pre_qualified', label: 'Pre-Qualified' },
  { key: 'approved', label: 'Approved' },
  { key: 'home_shopping', label: 'Home Shopping' },
  { key: 'under_contract', label: 'Under Contract' },
  { key: 'option_period', label: 'Option Period' },
  { key: 'loan_docs', label: 'Loan Docs' },
  { key: 'closing_disclosures', label: 'Closing Disclosures' },
  { key: 'final_review', label: 'Final Review' },
  { key: 'conditional_approval', label: 'Conditional Approval' },
  { key: 'clear_to_close', label: 'Clear to Close' },
  { key: 'closing', label: 'Closing' },
  { key: 'closed', label: 'Closed' },
];

const STATUS_OPTIONS = ['new', 'contacted', 'qualified', 'not_qualified', 'converted'];
const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-purple-100 text-purple-700',
  qualified: 'bg-green-100 text-green-700',
  not_qualified: 'bg-red-100 text-red-700',
  converted: 'bg-emerald-100 text-emerald-700',
  // 🔑 ITS OWN COLOUR, BECAUSE IT IS ITS OWN KIND OF ROW. A broker-submitted
  // file is a complete application staged in broker_submissions, waiting on the
  // borrower to register and confirm it. Rendering it as 'new' would put it in
  // a queue a rep is meant to call, when the thing it is waiting on is the
  // borrower finishing their own review.
  broker_submitted: 'bg-amber-100 text-amber-800',
};

// ⚠️ THE LABEL IS NOT THE RAW VALUE. 'broker_submitted' as a badge reads as
// jargon; what a rep needs is what the row is waiting for.
const STATUS_LABELS = { broker_submitted: 'Broker — awaiting borrower' };

// ── Utility helpers ──────────────────────────────────────────────────────────
function fmt$(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
}
function fmtDate(s) {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}
function decodeJwt(t) {
  try { return JSON.parse(atob(t.split('.')[1])); } catch { return null; }
}
async function downloadCSV(url, token, filename) {
  const res = await fetch(url, { headers: authHeaders(token) });
  if (!res.ok) return;
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── Small shared UI components ───────────────────────────────────────────────
function Spinner({ size = 5 }) {
  return (
  <svg className={`animate-spin w-${size} h-${size} text-blue-600`} fill="none" viewBox="0 0 24 24">
  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
  );
}

function StatCard({ label, value, sub, color }) {
  return (
  <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">{label}</p>
  <p className={`text-3xl font-bold ${color || 'text-gray-900'}`}>{value}</p>
  {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </div>
  );
}

function Badge({ text, className }) {
  return (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${className}`}>
  {text}
  </span>
  );
}

function EmptyState({ message }) {
  return (
  <div className="py-16 text-center text-gray-400">
  <svg className="w-10 h-10 mx-auto mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
  <p className="text-sm">{message}</p>
  </div>
  );
}

function TH({ children, onClick, sorted }) {
  return (
  <th
  onClick={onClick}
  className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap ${onClick ? 'cursor-pointer select-none hover:text-gray-800' : ''}`}
  >
  {children}{sorted ? (sorted === 'asc' ? ' ↑' : ' ↓') : ''}
  </th>
  );
}

function Pagination({ page, total, perPage, onChange }) {
  const pages = Math.ceil(total / perPage);
  if (pages <= 1) return null;
  return (
  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
  <span>{total} total</span>
  <div className="flex items-center gap-1">
  <button onClick={() => onChange(page - 1)} disabled={page <= 1} className="px-2 py-1 rounded disabled:opacity-30 hover:bg-gray-100">‹</button>
  {Array.from({ length: Math.min(pages, 5) }, (_, i) => {
  const p = pages <= 5 ? i + 1 : Math.max(1, page - 2) + i;
  if (p > pages) return null;
  return (
  <button key={p} onClick={() => onChange(p)} className={`px-2.5 py-1 rounded text-xs ${p === page ? 'bg-blue-600 text-white' : 'hover:bg-gray-100'}`}>{p}</button>
  );
  })}
  <button onClick={() => onChange(page + 1)} disabled={page >= pages} className="px-2 py-1 rounded disabled:opacity-30 hover:bg-gray-100">›</button>
  </div>
  </div>
  );
}

function SlidePanel({ open, onClose, title, children }) {
  return (
  <>
  {open && <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />}
  <div className={`fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}>
  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
  <h2 className="font-bold text-gray-900 text-lg">{title}</h2>
  <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
  </button>
  </div>
  <div className="flex-1 overflow-y-auto p-6">{children}</div>
  </div>
  </>
  );
}

// ── Overview Section ─────────────────────────────────────────────────────────
function OverviewSection({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    fetch(`${API}/api/admin/overview?${params}`, { headers: authHeaders(token) })
      .then(r => r.json()).then(setData).finally(() => setLoading(false));
  }, [token, startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) return <div className="flex justify-center py-20"><Spinner size={8} /></div>;
  if (!data) return <EmptyState message="Failed to load overview." />;

  const { metrics, charts } = data;

  return (
  <div className="space-y-6">

  {/* Date range filter */}
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
    <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Date Range</span>
    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
      style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit' }} />
    <span style={{ fontSize: '12px', color: '#9ca3af' }}>to</span>
    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
      style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit' }} />
    {(startDate || endDate) && (
      <button onClick={() => { setStartDate(''); setEndDate(''); }}
        style={{ padding: '7px 14px', background: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Clear</button>
    )}
    {loading && <span style={{ fontSize: '11px', color: '#9ca3af' }}>Updating...</span>}
  </div>

  {/* KPI Cards */}
  <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
  <StatCard label="Registered Users" value={metrics.registeredUsers?.toLocaleString() || 0} />
  <StatCard label="Total Applications" value={metrics.totalApplications?.toLocaleString() || 0} />
  <StatCard label="Approval Rate" value={`${metrics.approvalRate || 0}%`} color="text-emerald-600" />
  <StatCard label="Conversion Rate" value={`${metrics.conversionRate || 0}%`} color="text-blue-600" />
  <StatCard label="Avg Deal Size" value={fmt$(metrics.avgDealSize || 0)} />
  <StatCard label="Active Loans" value={metrics.activeLoans?.toLocaleString() || 0} color="text-blue-600" />
  <StatCard label="Realized Revenue" value={fmt$(metrics.realizedRevenue || 0)} color="text-emerald-600" />
  <StatCard label="Funded Volume" value={fmt$(metrics.fundedVolume || 0)} />
  <StatCard label="Active Partners" value={metrics.activePartners?.toLocaleString() || 0} />
  <StatCard label="Total Referrals" value={metrics.totalReferrals?.toLocaleString() || 0} />
  <StatCard label="Avg NPS Score" value={metrics.avgNPS || '—'} color={metrics.avgNPS && parseFloat(metrics.avgNPS) >= 8 ? 'text-emerald-600' : 'text-amber-600'} />
  <StatCard label="Avg Rating" value={metrics.avgRating ? `${metrics.avgRating}/5` : '—'} />
  </div>

  {/* Charts */}
  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
  <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
  <h3 className="font-semibold text-gray-800 mb-4">Application Funnel</h3>
  <ResponsiveContainer width="100%" height={250}>
  <BarChart data={charts.funnel} layout="vertical" margin={{ top: 2, right: 20, bottom: 0, left: 10 }}>
  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
  <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
  <YAxis type="category" dataKey="stage" tick={{ fontSize: 11, fontWeight: 600 }} width={100} />
  <Tooltip formatter={v => [v, 'Borrowers']} />
  <Bar dataKey="count" fill={BLUE} radius={[0, 4, 4, 0]} />
  </BarChart>
  </ResponsiveContainer>
  </div>

  <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
  <h3 className="font-semibold text-gray-800 mb-4">Loan Volume by Month</h3>
  <ResponsiveContainer width="100%" height={250}>
  <BarChart data={charts.volumeByMonth} margin={{ top: 2, right: 8, bottom: 0, left: -10 }}>
  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : v >= 1000 ? `$${(v/1000).toFixed(0)}K` : `$${v}`} />
  <Tooltip formatter={v => [fmt$(v), 'Volume']} />
  <Bar dataKey="volume" fill={BLUE} radius={[4, 4, 0, 0]} />
  </BarChart>
  </ResponsiveContainer>
  </div>
  </div>
  </div>
  );
}

// ── Leads Section ────────────────────────────────────────────────────────────
function LeadsSection({ token }) {
  const [leads, setLeads] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, perPage: 25 });
  const [filters, setFilters] = useState({ search: '', status: '', state: '', loan_type: '', credit_score_range: '' });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [noteInput, setNoteInput] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (page = 1) => {
  setLoading(true);
  const params = new URLSearchParams({ page, ...filters });
  Object.keys(filters).forEach(k => !filters[k] && params.delete(k));
  const r = await fetch(`${API}/api/admin/leads?${params}`, { headers: authHeaders(token) });
  const d = await r.json();
  setLeads(d.leads || []);
  setPagination({ page: d.page || 1, total: d.total || 0, perPage: d.perPage || 25 });
  setLoading(false);
  }, [token, filters]);

  useEffect(() => { load(1); }, [load]);

  const openLead = (lead) => { setSelected(lead); setNoteInput(lead.admin_note || ''); };

  const saveStatus = async (id, status) => {
  setSaving(true);
  await fetch(`${API}/api/admin/leads/${id}/status`, {
  method: 'PUT', headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
  body: JSON.stringify({ lead_status: status, admin_note: noteInput }),
  });
  setSaving(false);
  setLeads(prev => prev.map(l => l.id === id ? { ...l, lead_status: status, admin_note: noteInput } : l));
  if (selected?.id === id) setSelected(prev => ({ ...prev, lead_status: status, admin_note: noteInput }));
  };

  const saveNote = async () => {
  if (!selected) return;
  setSaving(true);
  await fetch(`${API}/api/admin/leads/${selected.id}/status`, {
  method: 'PUT', headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
  body: JSON.stringify({ lead_status: selected.lead_status, admin_note: noteInput }),
  });
  setSaving(false);
  setLeads(prev => prev.map(l => l.id === selected.id ? { ...l, admin_note: noteInput } : l));
  setSelected(prev => ({ ...prev, admin_note: noteInput }));
  };

  return (
  <div>
  {/* Filters */}
  <div className="flex flex-wrap gap-2 mb-4">
  <input type="text" placeholder="Search name / email / phone…" value={filters.search}
  onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
  className="h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 w-56" />
  {[
  { key: 'status', options: ['', ...STATUS_OPTIONS], label: 'All Statuses' },
  { key: 'state', options: ['', 'TX', 'FL', 'CA', 'NY', 'Other'], label: 'All States' },
  { key: 'loan_type', options: ['', 'conventional', 'fha', 'va', 'usda', 'jumbo'], label: 'All Loan Types' },
  { key: 'credit_score_range', options: ['', '580-619', '620-659', '660-699', '700-739', '740+'], label: 'All Credit Scores' },
  ].map(f => (
  <select key={f.key} value={filters[f.key]} onChange={e => setFilters(prev => ({ ...prev, [f.key]: e.target.value }))}
  className="h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 capitalize">
  <option value="">{f.label}</option>
  {f.options.slice(1).map(o => <option key={o} value={o}>{o}</option>)}
  </select>
  ))}
  <button onClick={() => downloadCSV(`${API}/api/admin/export/leads`, token, 'leads.csv')}
  className="h-9 px-3 rounded-lg border border-gray-200 text-sm hover:bg-gray-50 flex items-center gap-1.5">
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
  CSV
  </button>
  </div>

  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
  {loading ? (
  <div className="py-20 flex justify-center"><Spinner size={8} /></div>
  ) : leads.length === 0 ? (
  <EmptyState message="No leads match your filters." />
  ) : (
  <div className="overflow-x-auto">
  <table className="w-full text-sm">
  <thead className="bg-gray-50 border-b border-gray-100">
  <tr>
  <TH>Name</TH><TH>Contact</TH><TH>Loan Type</TH>
  <TH>Price</TH><TH>Credit</TH><TH>Status</TH><TH>Submitted</TH><TH></TH>
  </tr>
  </thead>
  <tbody className="divide-y divide-gray-50">
  {leads.map(lead => (
  <tr key={lead.id} className="hover:bg-gray-50 transition-colors">
  <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
  {lead.name}
  {/* 🚨 BROKER-SUBMITTED FILES WERE ON NO STAFF SCREEN AT ALL until this row
      existed — routes/admin.js never read broker_submissions. The figures on it
      are the BROKER'S unconfirmed claims, so the row says so rather than
      presenting them as the borrower's application. */}
  {lead.record_type === 'broker_submission' && (
  <div className="text-xs text-amber-700">Submitted by a broker · unconfirmed</div>
  )}
  </td>
  <td className="px-4 py-3 text-gray-600">
  <div className="whitespace-nowrap">{lead.email}</div>
  <div className="text-gray-400 text-xs">{lead.phone}</div>
  </td>
  <td className="px-4 py-3 text-gray-600 capitalize whitespace-nowrap">{lead.loan_type}</td>
  <td className="px-4 py-3 text-gray-700 font-medium whitespace-nowrap">{fmt$(lead.purchase_price)}</td>
  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{lead.credit_score_range}</td>
  <td className="px-4 py-3 whitespace-nowrap">
  <Badge text={STATUS_LABELS[lead.lead_status] || lead.lead_status || 'new'} className={STATUS_COLORS[lead.lead_status] || STATUS_COLORS.new} />
  </td>
  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{fmtDate(lead.created_at)}</td>
  <td className="px-4 py-3">
  <button onClick={() => openLead(lead)} className="text-xs text-blue-600 hover:underline">View</button>
  </td>
  </tr>
  ))}
  </tbody>
  </table>
  </div>
  )}
  <Pagination page={pagination.page} total={pagination.total} perPage={pagination.perPage} onChange={p => load(p)} />
  </div>

  <SlidePanel open={!!selected} onClose={() => setSelected(null)} title={selected?.name || ''}>
  {selected && (
  <div className="space-y-5">
  <div className="grid grid-cols-2 gap-3 text-sm">
  {[
  ['Email', selected.email], ['Phone', selected.phone],
  ['Loan Type', selected.loan_type], ['Purchase Price', fmt$(selected.purchase_price)],
  ['Down Payment', fmt$(selected.down_payment)], ['Credit Score', selected.credit_score_range],
  ['Employment', selected.employment_type], ['Timeline', selected.timeline],
  ['Property Type', selected.property_type], ['State', selected.state || ''],
  ].map(([k, v]) => (
  <div key={k}>
  <p className="text-xs text-gray-400 mb-0.5">{k}</p>
  <p className="font-medium text-gray-800 capitalize">{v}</p>
  </div>
  ))}
  </div>
  {/* 🔑 A broker submission opens its own record, never a borrower URL built from its id (Jesse, 2026-11-02). */}
  {selected.record_type === 'broker_submission' && selected.submission_id && (
  <a href={`/admin/partners/submissions/${Number(selected.submission_id)}`} className="inline-block text-sm font-semibold text-blue-600 hover:underline">
  Open submission →
  </a>
  )}
  {selected.notes && (
  <div>
  <p className="text-xs text-gray-400 mb-1">Lead Notes</p>
  <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{selected.notes}</p>
  </div>
  )}
  <div>
  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Update Status</p>
  <div className="flex flex-wrap gap-2">
  {STATUS_OPTIONS.map(s => (
  <button key={s} onClick={() => saveStatus(selected.id, s)} disabled={saving}
  className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize border transition-all ${selected.lead_status === s ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
  {s.replace('_', ' ')}
  </button>
  ))}
  </div>
  </div>
  <div>
  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Admin Note</p>
  <textarea rows={4} value={noteInput} onChange={e => setNoteInput(e.target.value)}
  className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-200" />
  <button onClick={saveNote} disabled={saving}
  className="mt-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
  style={{ backgroundColor: BLUE }}>
  {saving ? 'Saving…' : 'Save Note'}
  </button>
  </div>
  </div>
  )}
  </SlidePanel>
  </div>
  );
}

// ── Applications Section ─────────────────────────────────────────────────────
function ApplicationsSection({ token }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [noteInput, setNoteInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
  fetch(`${API}/api/admin/applications`, { headers: authHeaders(token) })
  .then(r => r.json()).then(d => setApps(d.applications || [])).finally(() => setLoading(false));
  }, [token]);

  const openApp = async (app) => {
  setSelected(app);
  setNoteInput(app.admin_note || '');
  const r = await fetch(`${API}/api/admin/applications/${app.id}`, { headers: authHeaders(token) });
  const d = await r.json();
  setDetail(d.application || null);
  };

  const saveNote = async () => {
  if (!selected) return;
  setSaving(true);
  await fetch(`${API}/api/admin/applications/${selected.id}/note`, {
  method: 'PUT', headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
  body: JSON.stringify({ admin_note: noteInput }),
  });
  setSaving(false);
  setApps(prev => prev.map(a => a.id === selected.id ? { ...a, admin_note: noteInput } : a));
  };

  return (
  <div>
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
  {loading ? (
  <div className="py-20 flex justify-center"><Spinner size={8} /></div>
  ) : apps.length === 0 ? (
  <EmptyState message="No applications yet." />
  ) : (
  <div className="overflow-x-auto">
  <table className="w-full text-sm">
  <thead className="bg-gray-50 border-b border-gray-100">
  <tr><TH>Applicant</TH><TH>Email</TH><TH>Co-Borrower</TH><TH>Employment</TH><TH>Completion</TH><TH>Submitted</TH><TH>Note</TH><TH></TH></tr>
  </thead>
  <tbody className="divide-y divide-gray-50">
  {apps.map(app => (
  <tr key={app.id} className="hover:bg-gray-50 transition-colors">
  <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{app.first_name} {app.last_name}</td>
  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{app.email}</td>
  <td className="px-4 py-3 whitespace-nowrap">
  {app.coborrower ? (
  <div>
  <p className="text-xs text-gray-700">{app.coborrower.first_name} {app.coborrower.last_name}</p>
  <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full mt-0.5 ${
  app.coborrower.status === 'accepted' ? 'bg-green-100 text-green-700' :
  app.coborrower.status === 'expired' ? 'bg-red-100 text-red-600' :
  'bg-yellow-100 text-yellow-700'
  }`}>
  {app.coborrower.status === 'accepted' ? 'Registered' : app.coborrower.status === 'expired' ? 'Expired' : 'Invited'}
  </span>
  </div>
  ) : (
  <span className="text-xs text-gray-300">None</span>
  )}
  </td>
  <td className="px-4 py-3 whitespace-nowrap">
  {(() => {
  const months = app.formDataPreview?.total_employment_months;
  if (!months && months !== 0) return <span className="text-xs text-gray-300">—</span>;
  const y = Math.floor(months / 12), m = months % 12;
  const label = y > 0 ? `${y}y ${m}m` : `${m}m`;
  const color = months >= 24 ? '#acf44e' : months >= 12 ? '#f59e0b' : '#ff082f';
  const bg = months >= 24 ? '#f0fde4' : months >= 12 ? '#fffbeb' : '#fff0f3';
  return (
  <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color, backgroundColor: bg }}>
  {label}{app.formDataPreview?.requires_employment_gap_lox ? ' ⚠️' : ''}
  </span>
  );
  })()}
  </td>
  <td className="px-4 py-3 whitespace-nowrap">
  <div className="flex items-center gap-2">
  <div className="flex-1 bg-gray-100 rounded-full h-1.5 w-24">
  <div className="h-1.5 rounded-full" style={{ width: `${app.completion}%`, backgroundColor: app.completion === 100 ? '#acf44e' : BLUE }} />
  </div>
  <span className="text-xs text-gray-500">{app.completion}%</span>
  </div>
  </td>
  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{fmtDate(app.created_at)}</td>
  <td className="px-4 py-3 text-gray-500 text-xs max-w-xs truncate">{app.admin_note || ''}</td>
  <td className="px-4 py-3">
  <button onClick={() => openApp(app)} className="text-xs text-blue-600 hover:underline">View</button>
  </td>
  </tr>
  ))}
  </tbody>
  </table>
  </div>
  )}
  </div>

  <SlidePanel open={!!selected} onClose={() => { setSelected(null); setDetail(null); }} title={selected ? `${selected.first_name} ${selected.last_name}` : ''}>
  {selected && (
  <div className="space-y-5">
  {!detail ? (
  <div className="flex justify-center py-8"><Spinner size={6} /></div>
  ) : (
  <div className="space-y-3">
  {/* ID type badge */}
  {(detail.formData?.id_type || detail.formData?.id_number) && (
  <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl p-3">
  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
  <div>
  <p className="text-xs text-gray-400">{detail.formData.id_type || 'ID'}</p>
  <p className="text-sm font-mono font-medium text-gray-800">{detail.formData.id_number || ''}</p>
  </div>
  </div>
  )}
  <div className="grid grid-cols-2 gap-2">
  {Object.entries(detail.formData || {})
  /* ── 🚨 AN ALLOW-LIST, BECAUSE A DENY-LIST FAILS OPEN ──────────────────
      This rendered EVERY key in form_data except a hand-maintained exclusion
      list, and that list named four keys: govt_ethnicity, govt_race, govt_sex,
      govt_info_provided. marital_status and the three date-of-birth parts were
      never on it, so they rendered — and §1002.6(b)(9) puts marital status and
      age in the same category as the four that were excluded.

      🔑 THE ASYMMETRY IS THE WHOLE ARGUMENT. A deny-list that misses an entry
      DISCLOSES a protected field; an allow-list that misses one fails to show
      an ordinary field. The first is a compliance problem found by audit, the
      second is a gap somebody notices in a day and asks about.

      ⚠️ IT IS THIS VIEW'S OWN LIST, deliberately. The alternative is importing
      the backend vocabulary into the browser, and a display list is not a
      description of the canonical record — a field added to the record should
      NOT appear here automatically, which is the behaviour being removed. */
  .filter(([k, v]) => [
    'first_name', 'last_name', 'name', 'email', 'phone',
    'address_street', 'address_city', 'address_state', 'address_zip',
    'address_duration', 'address_years', 'address_months', 'housing_status', 'citizenship',
    'employment_type', 'employment_types', 'job_title',
    'purchase_price', 'down_payment', 'loan_amount', 'loan_type',
    'property_type', 'credit_score_range', 'timeline', 'heard_about',
    'target_zips', 'target_zips_unconfirmed', 'buying_state',
    'has_ssn', 'coborrower_invited', 'coborrower_first_name',
    'coborrower_last_name', 'coborrower_email', 'coborrower_phone',
  ].includes(k)
  && v !== '' && v !== null && v !== undefined && typeof v !== 'object')
  .map(([k, v]) => (
  <div key={k} className="bg-gray-50 rounded-lg p-2">
  <p className="text-xs text-gray-400 capitalize">{k.replace(/_/g, ' ')}</p>
  <p className="text-sm font-medium text-gray-800 truncate">{String(v)}</p>
  </div>
  ))
  }
  </div>

  {/* LTV / Down Payment Summary */}
  {detail.formData?.purchase_price && (
  <div>
  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">LTV / Down Payment</p>
  <div className="rounded-xl border border-gray-100 divide-y divide-gray-100 overflow-hidden bg-gray-50">
  {(() => {
  const fd = detail.formData;
  const price = Number(fd.purchase_price) || 0;
  const down = fd.down_payment !== '' && fd.down_payment != null ? Number(fd.down_payment) : null;
  const dpPct = fd.down_payment_percentage != null ? fd.down_payment_percentage : (price > 0 && down !== null ? down / price : null);
  const ltv = fd.ltv_ratio != null ? fd.ltv_ratio : (price > 0 && down !== null ? (price - down) / price : null);
  const minPct = fd.minimum_dp_percentage_required != null ? fd.minimum_dp_percentage_required : null;
  const minAmt = fd.minimum_dp_amount_required != null ? fd.minimum_dp_amount_required : null;
  const meets = fd.dp_meets_requirement;
  const ltvColor = ltv == null ? '#6b7280' : ltv <= 0.80 ? '#acf44e' : ltv <= 0.90 ? '#d97706' : '#ff082f';
  const rows = [
  ['Purchase Price', fmt$(price)],
  ['Down Payment', down !== null ? fmt$(down) : '—'],
  ['Down Payment %', dpPct !== null ? `${(dpPct * 100).toFixed(1)}%` : '—'],
  ['Loan Amount', price > 0 && down !== null ? fmt$(price - down) : '—'],
  ['LTV Ratio', ltv !== null ? `${(ltv * 100).toFixed(1)}%` : '—', ltvColor],
  ['Min. DP Required', minPct !== null ? `${Math.round(minPct * 100)}%${minAmt != null ? ` (${fmt$(minAmt)})` : ''}` : '—'],
  ];
  return (
  <>
  {rows.map(([label, value, color]) => (
  <div key={label} className="flex items-center justify-between px-3 py-2 gap-3">
  <span className="text-xs text-gray-400">{label}</span>
  <span className="text-xs font-medium" style={{ color: color || '#1f2937' }}>{value}</span>
  </div>
  ))}
  <div className="flex items-center justify-between px-3 py-2 gap-3">
  <span className="text-xs text-gray-400">DP Meets Requirement</span>
  <span className={`text-xs font-semibold ${meets === true ? 'text-green-600' : meets === false ? 'text-red-600' : 'text-gray-400'}`}>
  {meets === true ? '✓ Yes' : meets === false ? '⚑ No' : '—'}
  </span>
  </div>
  </>
  );
  })()}
  </div>
  </div>
  )}

  {/* 🔑 Demographic information (URLA Section 8): the status only, never the answers (2026-10-27). The values go to
      the 1003 and the MISMO export; the server sends demographics_status (utils/urlaDemographics.toStaffView). */}
  <div data-testid="admin-drawer-demographics">
  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Demographic information (URLA Section 8)</p>
  <div className="rounded-xl border border-gray-100 overflow-hidden bg-gray-50">
  <div className="flex items-start justify-between gap-3 px-3 py-2">
  <span className="text-xs text-gray-400 flex-shrink-0">Status</span>
  <span className="text-xs font-medium text-gray-800 text-right">{detail.formData?.demographics_status || <span className="text-gray-300">Not answered yet</span>}</span>
  </div>
  </div>
  </div>
  </div>
  )}

  {/* Declarations — shown when application has been submitted through step 4 */}
  {detail.formData?.declarations && (
  <div>
  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Declarations</p>
  <div className="rounded-xl border border-gray-100 bg-gray-50 divide-y divide-gray-100 overflow-hidden">
  {(() => {
  const d = detail.formData.declarations;
  const behindVal = d?.behind_on_mortgage;
  const label = behindVal === true ? 'Yes' : behindVal === false ? 'No' : 'Not answered';
  const isYes = behindVal === true;
  return (
  <div className="flex items-center justify-between px-3 py-2.5 gap-3">
  <span className="text-xs text-gray-400">Behind on Mortgage / Default</span>
  <span className={`text-xs font-semibold flex items-center gap-1 ${isYes ? 'text-orange-600' : 'text-gray-600'}`}>
  {isYes && <span className="text-orange-500">⚑</span>}
  {label}
  </span>
  </div>
  );
  })()}
  </div>
  </div>
  )}

  <div>
  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Admin Note</p>
  <textarea rows={3} value={noteInput} onChange={e => setNoteInput(e.target.value)}
  className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-200" />
  <button onClick={saveNote} disabled={saving}
  className="mt-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
  style={{ backgroundColor: BLUE }}>
  {saving ? 'Saving…' : 'Save Note'}
  </button>
  </div>
  </div>
  )}
  </SlidePanel>
  </div>
  );
}

// ── Pipeline Section (Kanban) ────────────────────────────────────────────────
function PipelineSection({ token }) {
  const [columns, setColumns] = useState({});
  const [loading, setLoading] = useState(true);
  const dragId = useRef(null);

  const load = useCallback(() => {
  fetch(`${API}/api/admin/pipeline`, { headers: authHeaders(token) })
  .then(r => r.json()).then(d => {
  setColumns(d.pipeline || {});
  setLoading(false);
  });
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const onDrop = async (toStage) => {
  const id = dragId.current;
  if (!id) return;
  const fromStage = Object.keys(columns).find(s => columns[s]?.leads?.some(l => l.id === id));
  if (!fromStage || fromStage === toStage) return;
  const lead = columns[fromStage].leads.find(l => l.id === id);
  setColumns(prev => {
  const next = JSON.parse(JSON.stringify(prev));
  next[fromStage].leads = next[fromStage].leads.filter(l => l.id !== id);
  if (!next[toStage]) next[toStage] = { leads: [], totalValue: 0 };
  next[toStage].leads = [{ ...lead, loan_stage: toStage }, ...next[toStage].leads];
  return next;
  });
  await fetch(`${API}/api/admin/pipeline/${id}/stage`, {
  method: 'PUT', headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
  body: JSON.stringify({ loan_stage: toStage }),
  });
  };

  if (loading) return <div className="flex justify-center py-20"><Spinner size={8} /></div>;

  return (
  <div className="overflow-x-auto pb-4">
  <div className="flex gap-3 min-w-max">
  {PIPELINE_STAGES.map(({ key, label }) => {
  const col = columns[key] || { leads: [], totalValue: 0 };
  return (
  <div key={key} className="w-60 flex-shrink-0"
  onDragOver={e => e.preventDefault()}
  onDrop={() => onDrop(key)}>
  <div className="bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden">
  <div className="px-3 py-2.5 border-b border-gray-100 flex items-center justify-between">
  <div>
  <span className="text-sm font-semibold text-gray-800">{label}</span>
  <span className="ml-2 text-xs text-gray-400">{col.leads.length}</span>
  </div>
  {col.totalValue > 0 && <span className="text-xs text-gray-400">{fmt$(col.totalValue)}</span>}
  </div>
  <div className="p-2 space-y-2 min-h-[120px]">
  {col.leads.map(lead => (
  <div key={lead.id} draggable
  onDragStart={() => { dragId.current = lead.id; }}
  onDragEnd={() => { dragId.current = null; }}
  className="bg-white rounded-xl border border-gray-100 p-3 cursor-grab active:cursor-grabbing shadow-sm hover:shadow transition-shadow">
  <p className="text-sm font-medium text-gray-900 truncate">{lead.name}</p>
  <p className="text-xs text-gray-400 mt-0.5 truncate">{lead.email}</p>
  <p className="text-xs font-semibold mt-1" style={{ color: BLUE }}>{fmt$(lead.purchase_price)}</p>
  </div>
  ))}
  </div>
  </div>
  </div>
  );
  })}
  </div>
  </div>
  );
}

// ── Waitlist Section ─────────────────────────────────────────────────────────
function WaitlistSection({ token }) {
  const [entries, setEntries] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, perPage: 25 });
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState('');

  const load = useCallback(async (page = 1) => {
  setLoading(true);
  const params = new URLSearchParams({ page });
  if (stateFilter) params.set('state', stateFilter);
  const r = await fetch(`${API}/api/admin/waitlist?${params}`, { headers: authHeaders(token) });
  const d = await r.json();
  setEntries(d.entries || []);
  setPagination({ page: d.page || 1, total: d.total || 0, perPage: d.perPage || 25 });
  setStats(d.stats || {});
  setLoading(false);
  }, [token, stateFilter]);

  useEffect(() => { load(1); }, [load]);

  const markNotified = async (id) => {
  await fetch(`${API}/api/admin/waitlist/${id}/notified`, {
  method: 'PUT', headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
  body: JSON.stringify({ notified: 1 }),
  });
  setEntries(prev => prev.map(e => e.id === id ? { ...e, notified: 1 } : e));
  };

  return (
  <div className="space-y-4">
  <div className="grid grid-cols-3 gap-4">
  <StatCard label="Total Waitlist" value={stats.total || 0} />
  <StatCard label="Notified" value={stats.notified || 0} color="text-emerald-600" />
  <StatCard label="Pending" value={stats.pending || 0} color="text-amber-600" />
  </div>

  <div className="flex gap-2">
  <select value={stateFilter} onChange={e => setStateFilter(e.target.value)}
  className="h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200">
  <option value="">All States</option>
  {['TX', 'FL', 'CA', 'NY', 'Other'].map(s => <option key={s} value={s}>{s}</option>)}
  </select>
  <button onClick={() => downloadCSV(`${API}/api/admin/export/waitlist`, token, 'waitlist.csv')}
  className="h-9 px-3 rounded-lg border border-gray-200 text-sm hover:bg-gray-50 flex items-center gap-1.5">
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
  CSV
  </button>
  </div>

  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
  {loading ? (
  <div className="py-20 flex justify-center"><Spinner size={8} /></div>
  ) : entries.length === 0 ? (
  <EmptyState message="No waitlist entries." />
  ) : (
  <div className="overflow-x-auto">
  <table className="w-full text-sm">
  <thead className="bg-gray-50 border-b border-gray-100">
  <tr><TH>Name</TH><TH>Email</TH><TH>Phone</TH><TH>State</TH><TH>Signed Up</TH><TH>Notified</TH></tr>
  </thead>
  <tbody className="divide-y divide-gray-50">
  {entries.map(e => (
  <tr key={e.id} className="hover:bg-gray-50 transition-colors">
  <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{e.name}</td>
  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{e.email}</td>
  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{e.phone || ''}</td>
  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{e.state}</td>
  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{fmtDate(e.created_at)}</td>
  <td className="px-4 py-3 whitespace-nowrap">
  {e.notified ? (
  <Badge text="Notified" className="bg-green-100 text-green-700" />
  ) : (
  <button onClick={() => markNotified(e.id)}
  className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600">
  Mark Notified
  </button>
  )}
  </td>
  </tr>
  ))}
  </tbody>
  </table>
  </div>
  )}
  <Pagination page={pagination.page} total={pagination.total} perPage={pagination.perPage} onChange={p => load(p)} />
  </div>
  </div>
  );
}

// ── Users Section ────────────────────────────────────────────────────────────
function UsersSection({ token, currentUserId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
  fetch(`${API}/api/admin/users`, { headers: authHeaders(token) })
  .then(r => r.json()).then(d => setUsers(d.users || [])).finally(() => setLoading(false));
  }, [token]);

  const toggle = async (id, field, val) => {
  await fetch(`${API}/api/admin/users/${id}`, {
  method: 'PUT', headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
  body: JSON.stringify({ [field]: val ? 1 : 0 }),
  });
  setUsers(prev => prev.map(u => u.id === id ? { ...u, [field]: val ? 1 : 0 } : u));
  };

  return (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
  {loading ? (
  <div className="py-20 flex justify-center"><Spinner size={8} /></div>
  ) : users.length === 0 ? (
  <EmptyState message="No users yet." />
  ) : (
  <div className="overflow-x-auto">
  <table className="w-full text-sm">
  <thead className="bg-gray-50 border-b border-gray-100">
  <tr><TH>Name</TH><TH>Email</TH><TH>App Status</TH><TH>Step</TH><TH>Apps</TH><TH>Joined</TH><TH>Last Seen</TH><TH>Admin</TH><TH>Active</TH></tr>
  </thead>
  <tbody className="divide-y divide-gray-50">
  {users.map(u => (
  <tr key={u.id} className="hover:bg-gray-50 transition-colors">
  <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
  {u.first_name} {u.last_name}
  {u.id === currentUserId && <span className="ml-1.5 text-xs text-blue-500">(you)</span>}
  </td>
  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{u.email}</td>
  <td className="px-4 py-3 whitespace-nowrap">
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
  u.application_status === 'submitted' ? 'bg-green-100 text-green-700' :
  u.application_status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
  'bg-gray-100 text-gray-500'
  }`}>
  {u.application_status || 'not_started'}
  </span>
  </td>
  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
  {u.application_status === 'in_progress' ? `Step ${u.current_step || 1} of 5` : ''}
  </td>
  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{u.app_count || 0}</td>
  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{fmtDate(u.created_at)}</td>
  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{fmtDate(u.last_seen_at || u.last_login)}</td>
  <td className="px-4 py-3">
  {/* 🚨 READ-ONLY (hotfix-priv, 2026-11-01 security): PUT /api/admin/users/:id refuses is_admin and role (403). Staff
      access is granted only in Settings → Users by the two privileged staff, with a reason. */}
  <span className={`text-xs font-semibold ${u.is_admin ? 'text-doorly-navy' : 'text-gray-400'}`}>{u.is_admin ? 'Staff' : '—'}</span>
  </td>
  <td className="px-4 py-3">
  <button onClick={() => toggle(u.id, 'is_active', !u.is_active)}
  className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${u.is_active ? 'bg-green-500' : 'bg-gray-200'}`}>
  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${u.is_active ? 'translate-x-5' : 'translate-x-0.5'}`} />
  </button>
  </td>
  </tr>
  ))}
  </tbody>
  </table>
  </div>
  )}
  </div>
  );
}

// ── Identity Reviews Panel ────────────────────────────────────────────────────
// 🚨 READ ONLY (Jesse, 2026-10-28: "Remove Reject from both staff panels and send held IDs to the fraud queue"). No
// accept and no reject here: a held or flagged ID is a fraud-queue item and the file decision is made there (dismissing
// the last ID flag clears the ID). The list keeps the images and any rejection note already stored.
const ID_REVIEW_STATUS = {
  id_review_hold: { label: 'On hold', cls: 'bg-red-100 text-red-700' },
  flagged: { label: 'Flagged', cls: 'bg-yellow-100 text-yellow-700' },
  manual_review: { label: 'Manual review (earlier)', cls: 'bg-yellow-100 text-yellow-700' },
  rejected: { label: 'Rejected (earlier)', cls: 'bg-gray-100 text-gray-700' },
};
function IdentityReviewsPanel({ token }) {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/identity/admin/manual-reviews`, { headers: authHeaders(token) })
      .then(r => r.json()).then(d => setReviews(Array.isArray(d) ? d : [])).catch(() => setReviews([])).finally(() => setLoading(false));
  }, [token]);

  // 🚨 THE ID PHOTO OPENS THROUGH THE STAFF DOCUMENT ROUTE, BY DOCUMENT ID, WITH THE STAFF TOKEN (Jesse, 2026-10-28) —
  // never a /uploads link: that public mount is closed. The server resolves the document (routes/identity.js).
  const [msg, setMsg] = useState('');
  const openIdPhoto = async (userId, docId) => {
    if (!docId || !userId) return;
    setMsg('');
    const tab = window.open('', '_blank');
    try {
      const r = await fetch(`${API}/api/admin/borrowers/${userId}/documents/${docId}/view`, { headers: authHeaders(token) });
      if (!r.ok) throw new Error(String(r.status));
      const url = URL.createObjectURL(await r.blob());
      if (tab) tab.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (_) {
      if (tab) tab.close();
      setMsg('Could not open that ID photo.');
    }
  };

  if (loading) return <div className="flex justify-center py-10"><Spinner /></div>;

  const openQueue = () => navigate('/admin/modules/fraud-review');
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-gray-800">ID checks ({reviews.length})</h3>
            <p className="text-xs text-gray-400 mt-0.5">IDs the automatic check held or flagged. Decide them in the fraud review queue.</p>
          </div>
          <button type="button" data-testid="identity-reviews-open-queue" onClick={openQueue}
            className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
            Open the fraud review queue
          </button>
        </div>
        {reviews.length === 0 ? <EmptyState message="No held or flagged IDs." /> : (
          <div className="divide-y divide-gray-50">
            {reviews.map(r => {
              const st = ID_REVIEW_STATUS[r.status] || { label: r.status, cls: 'bg-gray-100 text-gray-700' };
              return (
                <div key={r.id} data-testid="identity-review-row" className="px-5 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{r.first_name} {r.last_name}</p>
                      <p className="text-xs text-gray-400">{r.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                        {r.manual_submitted_at && <span className="text-xs text-gray-400">{new Date(r.manual_submitted_at).toLocaleDateString()}</span>}
                      </div>
                      {r.admin_rejection_notes && <p className="text-xs text-orange-600 mt-1">Rejection note: {r.admin_rejection_notes}</p>}
                    </div>
                    <button onClick={() => setSelected(selected && selected.id === r.id ? null : r)}
                      className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
                      {selected && selected.id === r.id ? 'Hide images' : 'View images'}
                    </button>
                  </div>
                  {selected && selected.id === r.id && (
                    <div className="flex flex-wrap gap-3 mt-3">
                      {r.front_document_id && (
                        <button type="button" onClick={() => openIdPhoto(r.user_id, r.front_document_id)}
                          className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50">
                          🪪 View Front of ID
                        </button>
                      )}
                      {r.back_document_id && (
                        <button type="button" onClick={() => openIdPhoto(r.user_id, r.back_document_id)}
                          className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50">
                          🪪 View Back of ID
                        </button>
                      )}
                      {msg && <p className="w-full text-xs text-red-600">{msg}</p>}
                      {['id_review_hold', 'flagged'].includes(r.status) && (
                        <button type="button" data-testid={`identity-review-open-queue-${r.user_id}`} onClick={openQueue}
                          className="px-3 py-2 text-sm font-semibold rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
                          Decide in the fraud review queue
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Settings Section ─────────────────────────────────────────────────────────
// ── Documents Section ─────────────────────────────────────────────────────────
function DocumentsSection({ token, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('borrowers');
  const [netSheetRequests, setNetSheetRequests] = useState([]);
  const [vorRequests, setVorRequests] = useState([]);
  const [giftLetterRequests, setGiftLetterRequests] = useState([]);
  const [docFilter, setDocFilter] = useState('');
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [condForm, setCondForm] = useState({ user_id: '', condition_text: '', raised_by: 'AI Underwriter' });
  const [miscAdminItems, setMiscAdminItems] = useState([]);
  const [miscAddForm, setMiscAddForm] = useState({ user_id: '', document_name: '', description: '', document_type: 'other', required: true });
  const [miscAddSaving, setMiscAddSaving] = useState(false);
  const [miscAddMsg, setMiscAddMsg] = useState('');
  const [adminConvs, setAdminConvs] = useState([]);
  const [adminActiveConvId, setAdminActiveConvId] = useState(null);
  const [adminActiveConv, setAdminActiveConv] = useState(null);
  const [adminMessages, setAdminMessages] = useState([]);
  const [condSaving, setCondSaving] = useState(false);
  const [condMsg, setCondMsg] = useState('');
  const [adminUpload, setAdminUpload] = useState({ user_id: '', document_type: '', files: [] });
  const [uploading, setUploading] = useState(false);
  const adminUploadRef = useRef(null);

  useEffect(() => {
  setLoading(true);
  Promise.all([
  fetch(`${API}/api/documents/admin`, { headers: authHeaders(token) }).then(r => r.json()),
  fetch(`${API}/api/documents/net-sheet-requests`, { headers: authHeaders(token) }).then(r => r.json()).catch(() => ({ requests: [] })),
  fetch(`${API}/api/documents/vor-requests`, { headers: authHeaders(token) }).then(r => r.json()).catch(() => ({ requests: [] })),
  fetch(`${API}/api/gift-letter/admin-requests`, { headers: authHeaders(token) }).then(r => r.json()).catch(() => ({ requests: [] })),
  fetch(`${API}/api/documents/admin/miscellaneous`, { headers: authHeaders(token) }).then(r => r.json()).catch(() => ({ items: [] })),
  ]).then(([docsData, nsData, vorData, glData, miscData]) => {
  setData(docsData);
  setNetSheetRequests(nsData.requests || []);
  setVorRequests(vorData.requests || []);
  setGiftLetterRequests(glData.requests || []);
  setMiscAdminItems(miscData.items || []);
  setLoading(false);
  }).catch(() => setLoading(false));
  }, [token, refreshKey]);

  // Fetch admin message conversations when messages tab is active
  useEffect(() => {
  if (tab !== 'messages') return;
  fetch(`${API}/api/messages/admin/conversations`, { headers: authHeaders(token) })
    .then(r => r.json())
    .then(d => setAdminConvs(d.conversations || []))
    .catch(() => {});
  }, [tab, token]);

  useEffect(() => {
  if (!adminActiveConvId) return;
  fetch(`${API}/api/messages/admin/conversations/${adminActiveConvId}`, { headers: authHeaders(token) })
    .then(r => r.json())
    .then(d => { setAdminMessages(d.messages || []); setAdminActiveConv(d.conversation || null); })
    .catch(() => {});
  }, [adminActiveConvId, token]);

  const updateDocStatus = async (id, status, notes = '') => {
  setStatusUpdating(id);
  await fetch(`${API}/api/documents/admin/${id}/status`, {
  method: 'PUT',
  headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
  body: JSON.stringify({ status, notes }),
  });
  setStatusUpdating(null);
  // Refresh
  const d = await fetch(`${API}/api/documents/admin`, { headers: authHeaders(token) }).then(r => r.json());
  setData(d);
  };

  // 🔑 THE ONE STAFF COMPOSER (fp-format, Jesse 2026-11-01). payload = { body, format: 'rich', attachment_data?,
  // attachment_name? } from StaffComposer.
  //
  // 🚨 THE OLD ATTACH BUTTON NEVER WORKED, for two reasons in a row:
  //   1. the upload route answers { data, mime, name, size } and has never returned a `url`, so `if (r.ok && d.url)`
  //      was always false and the picked file was silently dropped — the reply then went out without it;
  //   2. the reply sent attachment_url/attachment_name, but the staff reply route stores only attachment_data (held to
  //      the borrower upload rules), so even a `url` would have been refused or ignored.
  //   (Before push C the upload route also refused a staff token outright.)
  // StaffComposer uploads through the same route, keeps { data, mime, name } and sends attachment_data +
  // attachment_name, which is what the route checks and stores. Resolves true when sent; a refusal throws so the
  // composer shows it and keeps the draft.
  const handleAdminReply = async (payload) => {
    if (!adminActiveConvId) return false;
    const r = await fetch(`${API}/api/messages/admin/conversations/${adminActiveConvId}`, {
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || 'The message was not sent.');
    setAdminMessages(prev => [...prev, d.message]);
    const lastSummary = (d.message && d.message.body) || (payload.attachment_name ? `📎 ${payload.attachment_name}` : '');
    setAdminConvs(prev => prev.map(c => c.id === adminActiveConvId
      ? { ...c, last_message_body: lastSummary, unread_count: 0 }
      : c
    ));
    return true;
  };

  const addCondition = async () => {
  if (!condForm.user_id || !condForm.condition_text) return;
  setCondSaving(true); setCondMsg('');
  const r = await fetch(`${API}/api/documents/admin/conditions`, {
  method: 'POST',
  headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
  body: JSON.stringify(condForm),
  });
  const d = await r.json();
  setCondSaving(false);
  setCondMsg(d.error || 'Condition added.');
  if (!d.error) setCondForm(f => ({ ...f, condition_text: '' }));
  const fresh = await fetch(`${API}/api/documents/admin`, { headers: authHeaders(token) }).then(r2 => r2.json());
  setData(fresh);
  };

  const adminUploadDocs = async () => {
  if (!adminUpload.user_id || !adminUpload.document_type || !adminUpload.files.length) return;
  setUploading(true);
  const fd = new FormData();
  fd.append('user_id', adminUpload.user_id);
  fd.append('document_type', adminUpload.document_type);
  adminUpload.files.forEach(f => fd.append('files', f));
  await fetch(`${API}/api/documents/admin/upload`, { method: 'POST', headers: authHeaders(token), body: fd });
  setUploading(false);
  setAdminUpload({ user_id: '', document_type: '', files: [] });
  const fresh = await fetch(`${API}/api/documents/admin`, { headers: authHeaders(token) }).then(r => r.json());
  setData(fresh);
  };

  const downloadDoc = async (doc) => {
  const res = await fetch(`${API}/api/documents/download/${doc.id}`, { headers: authHeaders(token) });
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = doc.original_name; a.click();
  URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center py-20"><Spinner size={8} /></div>;
  if (!data) return <EmptyState message="Failed to load documents." />;

  const { documents, conditions, userCompletions } = data;
  const filteredDocs = documents.filter(d =>
  !docFilter || d.document_type.includes(docFilter) || `${d.first_name} ${d.last_name}`.toLowerCase().includes(docFilter.toLowerCase())
  );
  const statusColors = {
  uploaded: 'bg-green-100 text-green-700',
  approved: 'bg-blue-100 text-blue-700',
  rejected: 'bg-red-100 text-red-700',
  action_required: 'bg-orange-100 text-orange-700',
  pending: 'bg-gray-100 text-gray-500',
  };

  return (
  <div className="space-y-5">
  {/* Tabs */}
  <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
  {[['borrowers', 'Borrower Progress'], ['docs', 'All Documents'], ['identity_reviews', 'Identity Reviews'], ['conditions', 'Conditions'], ['upload', 'Upload Docs'], ['net_sheet', 'Net Sheet Requests'], ['vor', 'VOR Requests'], ['gift_letters', 'Gift Letters'], ['asset_sources', 'Asset Sources'], ['misc_docs', 'Misc Docs'], ['messages', 'Messages'], ['surveys', 'Surveys']].map(([id, label]) => (
  <button key={id} onClick={() => setTab(id)}
  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>
  {label}
  </button>
  ))}
  </div>

  {/* Borrower Progress Tab */}
  {tab === 'borrowers' && (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
  <div className="px-5 py-4 border-b border-gray-50">
  <h3 className="font-semibold text-gray-800">Document Completion by Borrower</h3>
  </div>
  {userCompletions.length === 0 ? <EmptyState message="No borrowers yet." /> : (
  <div className="overflow-x-auto">
  <table className="w-full text-sm">
  <thead className="bg-gray-50 border-b border-gray-100">
  <tr>
  <TH>Borrower</TH><TH>Email</TH><TH>Documents</TH><TH>Completion</TH>
  </tr>
  </thead>
  <tbody className="divide-y divide-gray-50">
  {userCompletions.map(u => (
  <tr key={u.id} className="hover:bg-gray-50">
  <td className="px-4 py-3 font-medium text-gray-900">{u.first_name} {u.last_name}</td>
  <td className="px-4 py-3 text-gray-500 text-xs">{u.email}</td>
  <td className="px-4 py-3 text-gray-700">{u.doc_count}</td>
  <td className="px-4 py-3">
  <div className="flex items-center gap-2">
  <div className="flex-1 h-2 bg-gray-100 rounded-full max-w-[120px] overflow-hidden">
  <div className="h-full rounded-full transition-all"
  style={{ width: `${u.completion}%`, backgroundColor: u.completion >= 100 ? '#acf44e' : '#3a6ad9' }} />
  </div>
  <span className={`text-xs font-semibold ${u.completion >= 100 ? 'text-green-600' : 'text-gray-700'}`}>
  {u.completion}%
  </span>
  </div>
  </td>
  </tr>
  ))}
  </tbody>
  </table>
  </div>
  )}
  </div>
  )}

  {/* All Documents Tab */}
  {tab === 'docs' && (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
  <div className="px-5 py-4 border-b border-gray-50 flex items-center gap-3">
  <h3 className="font-semibold text-gray-800 flex-1">All Uploaded Documents</h3>
  <input
  value={docFilter} onChange={e => setDocFilter(e.target.value)}
  placeholder="Filter by name or type…"
  className="h-8 px-3 rounded-xl border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-blue-200 w-48"
  />
  </div>
  {filteredDocs.length === 0 ? <EmptyState message="No documents uploaded yet." /> : (
  <div className="overflow-x-auto">
  <table className="w-full text-sm">
  <thead className="bg-gray-50 border-b border-gray-100">
  <tr>
  <TH>Borrower</TH><TH>Type</TH><TH>File</TH><TH>Uploaded</TH><TH>Status</TH><TH>Actions</TH>
  </tr>
  </thead>
  <tbody className="divide-y divide-gray-50">
  {filteredDocs.map(d => (
  <tr key={d.id} className="hover:bg-gray-50">
  <td className="px-4 py-2.5 font-medium text-gray-900 whitespace-nowrap">
  {d.first_name} {d.last_name}
  <p className="text-xs text-gray-400 font-normal">{d.email}</p>
  </td>
  <td className="px-4 py-2.5 text-xs text-gray-600">{d.document_type.replace(/_/g, ' ')}</td>
  <td className="px-4 py-2.5 text-xs text-gray-700 max-w-[160px] truncate">{d.original_name}</td>
  <td className="px-4 py-2.5 text-xs text-gray-400 whitespace-nowrap">{fmtDate(d.upload_date)}</td>
  <td className="px-4 py-2.5">
  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${statusColors[d.status] || 'bg-gray-100 text-gray-600'}`}>
  {d.status}
  </span>
  </td>
  <td className="px-4 py-2.5">
  <div className="flex items-center gap-1.5">
  <button onClick={() => downloadDoc(d)} title="Download"
  className="text-xs text-[#3a6ad9] hover:underline">Download</button>
  <select
  value={d.status}
  onChange={e => updateDocStatus(d.id, e.target.value)}
  disabled={statusUpdating === d.id}
  className="h-7 text-xs rounded-lg border border-gray-200 px-1 focus:outline-none"
  >
  {['uploaded','approved','rejected','action_required'].map(s => (
  <option key={s} value={s}>{s}</option>
  ))}
  </select>
  </div>
  </td>
  </tr>
  ))}
  </tbody>
  </table>
  </div>
  )}
  </div>
  )}

  {/* Identity Reviews Tab */}
  {tab === 'identity_reviews' && (
  <IdentityReviewsPanel token={token} />
  )}

  {/* Conditions Tab */}
  {tab === 'conditions' && (
  <div className="space-y-4">
  {/* Add condition */}
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
  <h3 className="font-semibold text-gray-800 mb-4">Add Condition</h3>
  <div className="space-y-3">
  <div>
  <label className="text-xs font-medium text-gray-500 mb-1 block">Borrower</label>
  <select value={condForm.user_id} onChange={e => setCondForm(f => ({ ...f, user_id: e.target.value }))}
  className="w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200">
  <option value="">Select borrower…</option>
  {userCompletions.map(u => (
  <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>
  ))}
  </select>
  </div>
  <div>
  <label className="text-xs font-medium text-gray-500 mb-1 block">Condition</label>
  <textarea rows={2} value={condForm.condition_text}
  onChange={e => setCondForm(f => ({ ...f, condition_text: e.target.value }))}
  placeholder="e.g. Large deposit of $5,000 on 3/15 requires explanation"
  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-200" />
  </div>
  <div>
  <label className="text-xs font-medium text-gray-500 mb-1 block">Raised By</label>
  <select value={condForm.raised_by} onChange={e => setCondForm(f => ({ ...f, raised_by: e.target.value }))}
  className="w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200">
  <option>AI Underwriter</option>
  <option>Doorly</option>
  </select>
  </div>
  {condMsg && <p className="text-xs text-green-600">{condMsg}</p>}
  <button onClick={addCondition} disabled={condSaving}
  className="btn-primary text-white hover:opacity-90 disabled:opacity-50"
  style={{ backgroundColor: BLUE }}>
  {condSaving ? 'Adding…' : (
  <>
  <span>Add Condition</span>
  <span className="btn-arrow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg></span>
  </>
  )}
  </button>
  </div>
  </div>

  {/* All conditions */}
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
  <div className="px-5 py-4 border-b border-gray-50">
  <h3 className="font-semibold text-gray-800">All Conditions</h3>
  </div>
  {conditions.length === 0 ? <EmptyState message="No conditions yet." /> : (
  <div className="overflow-x-auto">
  <table className="w-full text-sm">
  <thead className="bg-gray-50 border-b border-gray-100">
  <tr><TH>Borrower</TH><TH>Condition</TH><TH>Raised By</TH><TH>Date</TH><TH>Status</TH></tr>
  </thead>
  <tbody className="divide-y divide-gray-50">
  {conditions.map(c => (
  <tr key={c.id} className="hover:bg-gray-50">
  <td className="px-4 py-2.5 font-medium text-gray-900 whitespace-nowrap">
  {c.first_name} {c.last_name}
  </td>
  <td className="px-4 py-2.5 text-sm text-gray-700 max-w-[240px]">{c.condition_text}</td>
  <td className="px-4 py-2.5 text-xs text-gray-500">{c.raised_by}</td>
  <td className="px-4 py-2.5 text-xs text-gray-400">{fmtDate(c.raised_date)}</td>
  <td className="px-4 py-2.5">
  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
  c.status === 'open' ? 'bg-orange-100 text-orange-700'
  : c.status === 'responded' ? 'bg-blue-100 text-blue-700'
  : 'bg-green-100 text-green-700'
  }`}>{c.status}</span>
  </td>
  </tr>
  ))}
  </tbody>
  </table>
  </div>
  )}
  </div>
  </div>
  )}

  {/* Upload Docs for Borrower Tab */}
  {tab === 'upload' && (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 max-w-lg">
  <h3 className="font-semibold text-gray-800 mb-4">Upload Loan Documents for Borrower</h3>
  <div className="space-y-3">
  <div>
  <label className="text-xs font-medium text-gray-500 mb-1 block">Borrower</label>
  <select value={adminUpload.user_id} onChange={e => setAdminUpload(f => ({ ...f, user_id: e.target.value }))}
  className="w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200">
  <option value="">Select borrower…</option>
  {userCompletions.map(u => (
  <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>
  ))}
  </select>
  </div>
  <div>
  <label className="text-xs font-medium text-gray-500 mb-1 block">Document Type</label>
  <select value={adminUpload.document_type} onChange={e => setAdminUpload(f => ({ ...f, document_type: e.target.value }))}
  className="w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200">
  <option value="">Select type…</option>
  <option value="loan_estimate">Loan Estimate</option>
  <option value="closing_disclosure">Closing Disclosure</option>
  <option value="initial_disclosures">Initial Disclosures</option>
  <option value="appraisal">Appraisal</option>
  <option value="other_admin">Other</option>
  </select>
  </div>
  <div>
  <label className="text-xs font-medium text-gray-500 mb-1 block">File</label>
  <input ref={adminUploadRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png"
  onChange={e => setAdminUpload(f => ({ ...f, files: Array.from(e.target.files) }))}
  className="hidden" />
  <button onClick={() => adminUploadRef.current?.click()}
  className="w-full h-10 rounded-xl border-2 border-dashed border-gray-200 text-xs text-gray-500 hover:border-[#3a6ad9] hover:text-[#3a6ad9] transition">
  {adminUpload.files.length ? `${adminUpload.files.length} file(s) selected` : 'Click to select files'}
  </button>
  </div>
  <button onClick={adminUploadDocs} disabled={uploading}
  className="btn-primary text-white hover:opacity-90 disabled:opacity-50"
  style={{ backgroundColor: BLUE }}>
  {uploading ? 'Uploading…' : (
  <>
  <span>Upload to Borrower Portal</span>
  <span className="btn-arrow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg></span>
  </>
  )}
  </button>
  </div>
  </div>
  )}

  {/* Net Sheet Requests Tab */}
  {tab === 'net_sheet' && (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
  <div className="px-5 py-4 border-b border-gray-50">
  <h3 className="font-semibold text-gray-800">Net Sheet Requests</h3>
  <p className="text-xs text-gray-400 mt-0.5">Requests sent by borrowers to their escrow agents</p>
  </div>
  {netSheetRequests.length === 0 ? (
  <div className="px-5 py-8 text-center text-sm text-gray-400">No net sheet requests yet.</div>
  ) : (
  <div className="overflow-x-auto">
  <table className="w-full text-sm">
  <thead className="bg-gray-50 border-b border-gray-100">
  <tr>
  {['Borrower', 'Escrow Agent', 'Agent Email', 'Property Address', 'Date Sent'].map(h => (
  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
  ))}
  </tr>
  </thead>
  <tbody className="divide-y divide-gray-50">
  {netSheetRequests.map(r => (
  <tr key={r.id} className="hover:bg-gray-50 transition-colors">
  <td className="px-4 py-3 whitespace-nowrap">
  <p className="font-medium text-gray-900">{r.first_name} {r.last_name}</p>
  <p className="text-xs text-gray-400">{r.borrower_email}</p>
  </td>
  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{r.escrow_agent_name}</td>
  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{r.escrow_agent_email}</td>
  <td className="px-4 py-3 text-gray-600 max-w-[220px] truncate">{r.property_address}</td>
  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
  {r.sent_at ? new Date(r.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
  </td>
  </tr>
  ))}
  </tbody>
  </table>
  </div>
  )}
  </div>
  )}

  {/* VOR Requests Tab */}
  {tab === 'vor' && (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
  <div className="px-5 py-4 border-b border-gray-50">
  <h3 className="font-semibold text-gray-800">VOR Requests</h3>
  <p className="text-xs text-gray-400 mt-0.5">Verification of Rent requests sent by borrowers to their landlords</p>
  </div>
  {vorRequests.length === 0 ? (
  <div className="px-5 py-8 text-center text-sm text-gray-400">No VOR requests yet.</div>
  ) : (
  <div className="overflow-x-auto">
  <table className="w-full text-sm">
  <thead className="bg-gray-50 border-b border-gray-100">
  <tr>
  {['Borrower', 'Landlord', 'Landlord Email', 'Property Address', 'Date Sent'].map(h => (
  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
  ))}
  </tr>
  </thead>
  <tbody className="divide-y divide-gray-50">
  {vorRequests.map(r => (
  <tr key={r.id} className="hover:bg-gray-50 transition-colors">
  <td className="px-4 py-3 whitespace-nowrap">
  <p className="font-medium text-gray-900">{r.first_name} {r.last_name}</p>
  <p className="text-xs text-gray-400">{r.borrower_email}</p>
  </td>
  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{r.landlord_name}</td>
  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{r.landlord_email}</td>
  <td className="px-4 py-3 text-gray-600 max-w-[220px] truncate">{r.property_address}</td>
  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
  {r.sent_at ? new Date(r.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
  </td>
  </tr>
  ))}
  </tbody>
  </table>
  </div>
  )}
  </div>
  )}

  {/* Gift Letters Tab */}
  {tab === 'gift_letters' && (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
  <div className="px-5 py-4 border-b border-gray-50">
  <h3 className="font-semibold text-gray-800">Gift Letter Requests</h3>
  <p className="text-xs text-gray-400 mt-0.5">Donor gift letter requests sent by borrowers</p>
  </div>
  {giftLetterRequests.length === 0 ? (
  <div className="px-5 py-8 text-center text-sm text-gray-400">No gift letter requests yet.</div>
  ) : (
  <div className="overflow-x-auto">
  <table className="w-full text-sm">
  <thead className="bg-gray-50 border-b border-gray-100">
  <tr>
  {['Borrower', 'Donor', 'Donor Email', 'Gift Amount', 'Status', 'Sent', 'Completed'].map(h => (
  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
  ))}
  </tr>
  </thead>
  <tbody className="divide-y divide-gray-50">
  {giftLetterRequests.map(r => (
  <tr key={r.id} className="hover:bg-gray-50 transition-colors">
  <td className="px-4 py-3 whitespace-nowrap">
  <p className="font-medium text-gray-900">{r.first_name} {r.last_name}</p>
  <p className="text-xs text-gray-400">{r.borrower_email}</p>
  </td>
  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{r.donor_name}</td>
  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{r.donor_email}</td>
  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
  {r.gift_amount ? `$${Number(r.gift_amount).toLocaleString()}` : '—'}
  </td>
  <td className="px-4 py-3 whitespace-nowrap">
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${
  r.status === 'completed'
  ? 'bg-green-100 text-green-700 border-green-200'
  : 'bg-yellow-100 text-yellow-700 border-yellow-200'
  }`}>
  {r.status === 'completed' ? '✅ Complete' : '⏳ Pending'}
  </span>
  </td>
  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
  {r.sent_at ? new Date(r.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
  </td>
  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
  {r.completed_at ? new Date(r.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
  </td>
  </tr>
  ))}
  </tbody>
  </table>
  </div>
  )}
  </div>
  )}

  {/* Asset Sources Tab */}
  {tab === 'asset_sources' && (
  <AssetSourcesAdminTab token={token} />
  )}

  {/* Misc Docs Tab */}
  {tab === 'messages' && (
  <div className="space-y-4">
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" style={{ height: '600px', display: 'flex' }}>
    {/* Conversation list */}
    <div style={{ width: '300px', borderRight: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
        <span className="text-sm font-semibold text-gray-700">All Conversations</span>
        <span className="ml-2 text-xs text-gray-400">{adminConvs.length}</span>
      </div>
      <div className="overflow-y-auto flex-1">
        {adminConvs.length === 0 ? (
          <div className="p-4 text-center text-gray-400 text-sm">No conversations.</div>
        ) : adminConvs.map(conv => (
          <button
            key={conv.id}
            onClick={() => setAdminActiveConvId(conv.id)}
            className={`w-full text-left px-4 py-3 border-b border-gray-50 transition-colors ${adminActiveConvId === conv.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
          >
            <div className="flex items-start justify-between gap-1">
              <span className="text-sm font-semibold text-gray-900 truncate">{conv.first_name} {conv.last_name}</span>
              {conv.unread_count > 0 && <span className="flex-shrink-0 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">{conv.unread_count}</span>}
            </div>
            <p className="text-xs text-gray-500 truncate mt-0.5">{conv.subject}</p>
            <p className="text-xs text-gray-400 truncate mt-0.5">{conv.last_message_body || ''}</p>
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium mt-1 inline-block ${conv.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{conv.status}</span>
          </button>
        ))}
      </div>
    </div>
    {/* Thread */}
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {adminActiveConv ? (
        <>
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <div>
              <span className="text-sm font-semibold text-gray-900">{adminActiveConv.subject}</span>
              <span className="text-xs text-gray-400 ml-2">{adminActiveConv.first_name} {adminActiveConv.last_name} ({adminActiveConv.user_email})</span>
            </div>
            <button
              onClick={async () => {
                const newStatus = adminActiveConv.status === 'open' ? 'closed' : 'open';
                await fetch(`${API}/api/messages/admin/conversations/${adminActiveConvId}/status`, {
                  method: 'PUT',
                  headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
                  body: JSON.stringify({ status: newStatus }),
                });
                setAdminActiveConv(prev => ({ ...prev, status: newStatus }));
                setAdminConvs(prev => prev.map(c => c.id === adminActiveConvId ? { ...c, status: newStatus } : c));
              }}
              className={`text-xs px-3 py-1 rounded-full font-medium border transition-colors ${adminActiveConv.status === 'open' ? 'bg-white border-gray-300 text-gray-600 hover:bg-gray-100' : 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100'}`}
            >
              {adminActiveConv.status === 'open' ? 'Close' : 'Reopen'}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {adminMessages.map(msg => {
              // Three-way. The old two-way form put every non-support row on the
              // borrower side, so an Eva reply would be read by staff as their
              // client's own words. isOutbound keeps her on the Doorly side; the
              // isEva label and fill stop her reading as a colleague.
              const isEva = msg.sender_role === 'eva';
              const isSupport = msg.sender_role === 'support';
              const isOutbound = isSupport || isEva;
              // 🚨 NO /uploads HREF (2026-10-28): an attachment opens through the signed attachment route (MessageAttachment).
              const hasAttachment = !!(msg.has_attachment && msg.attachment_name);
              return (
                <div key={msg.id} className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
                  <div style={{ maxWidth: '70%' }}>
                    {!isOutbound && <div className="text-xs text-gray-400 mb-1">{adminActiveConv.first_name}</div>}
                    {isEva && <div className="text-xs font-bold text-blue-600 mb-1 text-right">Eva · AI assistant</div>}
                    <div className={`px-3 py-2 rounded-xl text-sm leading-relaxed ${
                      isEva ? 'bg-blue-50 border border-blue-100 text-gray-800'
                            : isSupport ? 'bg-blue-600 text-white'
                                        : 'bg-white border border-gray-200 text-gray-800'}`}>
                      {hasRichBody(msg)
                        ? <RichMessageBody msg={msg} dark={isSupport} />
                        : msg.body && <p className="whitespace-pre-wrap">{msg.body}</p>}
                      {hasAttachment && (
                        <div className="mt-1"><MessageAttachment msgId={msg.id} name={msg.attachment_name} token={token} dark={isSupport} /></div>
                      )}
                    </div>
                    <div className={`text-xs text-gray-400 mt-1 ${isOutbound ? 'text-right' : ''}`}>
                      {new Date(msg.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {adminActiveConv.status === 'open' ? (
            <div className="px-4 py-3 border-t border-gray-100">
              <StaffComposer key={adminActiveConvId} token={token} onSend={handleAdminReply} testId="dashboard-reply-composer"
                placeholder="Reply as support… (⌘↵ to send)" sendLabel="Send" />
            </div>
          ) : (
            <div className="px-4 py-3 border-t border-gray-100 text-sm text-gray-400 text-center bg-gray-50">Conversation closed.</div>
          )}
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Select a conversation</div>
      )}
    </div>
  </div>
  </div>
  )}

  {tab === 'surveys' && (
  <SurveysAdminPanel token={token} />
  )}

  {tab === 'misc_docs' && (
  <div className="space-y-4">
  {/* Add Document Request */}
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
  <h3 className="font-semibold text-gray-800 mb-4">Add Document Request</h3>
  <div className="space-y-3">
  <div>
  <label className="text-xs font-medium text-gray-500 mb-1 block">Borrower</label>
  <select value={miscAddForm.user_id} onChange={e => setMiscAddForm(f => ({ ...f, user_id: e.target.value }))}
  className="w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200">
  <option value="">Select borrower…</option>
  {userCompletions.map(u => (
  <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>
  ))}
  </select>
  </div>
  <div>
  <label className="text-xs font-medium text-gray-500 mb-1 block">Document Name</label>
  <input
  value={miscAddForm.document_name}
  onChange={e => setMiscAddForm(f => ({ ...f, document_name: e.target.value }))}
  placeholder="e.g. Large Deposit Explanation - $5,000"
  className="w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
  />
  </div>
  <div>
  <label className="text-xs font-medium text-gray-500 mb-1 block">Instructions for Borrower</label>
  <textarea
  rows={2}
  value={miscAddForm.description}
  onChange={e => setMiscAddForm(f => ({ ...f, description: e.target.value }))}
  placeholder="e.g. Please provide a letter explaining the $5,000 deposit on 03/15/2024"
  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 resize-none"
  />
  </div>
  <div className="flex gap-3">
  <div className="flex-1">
  <label className="text-xs font-medium text-gray-500 mb-1 block">Document Type</label>
  <select value={miscAddForm.document_type} onChange={e => setMiscAddForm(f => ({ ...f, document_type: e.target.value }))}
  className="w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200">
  <option value="other">Other</option>
  <option value="letter_of_explanation">Letter of Explanation</option>
  <option value="bank_statement">Bank Statement</option>
  <option value="supporting_document">Supporting Document</option>
  </select>
  </div>
  <div className="flex-shrink-0">
  <label className="text-xs font-medium text-gray-500 mb-1 block">Required</label>
  <button
  type="button"
  onClick={() => setMiscAddForm(f => ({ ...f, required: !f.required }))}
  className={`h-10 px-4 rounded-xl text-sm font-semibold border transition ${miscAddForm.required ? 'bg-red-50 border-red-300 text-red-700' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
  {miscAddForm.required ? 'Required' : 'Optional'}
  </button>
  </div>
  </div>
  <div className="flex items-center gap-3">
  <button
  onClick={async () => {
  if (!miscAddForm.user_id || !miscAddForm.document_name.trim()) { setMiscAddMsg('Borrower and document name are required.'); return; }
  setMiscAddSaving(true); setMiscAddMsg('');
  const r = await fetch(`${API}/api/documents/admin/miscellaneous`, {
  method: 'POST',
  headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
  body: JSON.stringify(miscAddForm),
  });
  const d = await r.json();
  setMiscAddSaving(false);
  if (d.error) { setMiscAddMsg(d.error); return; }
  setMiscAddMsg('Document request added.');
  setMiscAddForm(f => ({ ...f, document_name: '', description: '' }));
  const fresh = await fetch(`${API}/api/documents/admin/miscellaneous`, { headers: authHeaders(token) }).then(r2 => r2.json()).catch(() => ({ items: [] }));
  setMiscAdminItems(fresh.items || []);
  }}
  disabled={miscAddSaving}
  className="h-10 px-5 rounded-xl text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60 transition"
  style={{ backgroundColor: '#3a6ad9' }}>
  {miscAddSaving ? 'Adding…' : 'Add to Borrower File'}
  </button>
  {miscAddMsg && <p className={`text-xs ${miscAddMsg.includes('added') ? 'text-green-600' : 'text-red-600'}`}>{miscAddMsg}</p>}
  </div>
  </div>
  </div>

  {/* Existing Requests */}
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
  <div className="px-5 py-4 border-b border-gray-50">
  <h3 className="font-semibold text-gray-800">All Miscellaneous Requests</h3>
  <p className="text-xs text-gray-400 mt-0.5">Document requests added by the loan officer team</p>
  </div>
  {miscAdminItems.length === 0 ? (
  <div className="px-5 py-8 text-center text-sm text-gray-400">No miscellaneous requests yet.</div>
  ) : (
  <div className="overflow-x-auto">
  <table className="w-full text-sm">
  <thead className="bg-gray-50 border-b border-gray-100">
  <tr>
  {['Borrower', 'Document', 'Type', 'Required', 'Status', 'Added', 'Actions'].map(h => (
  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
  ))}
  </tr>
  </thead>
  <tbody className="divide-y divide-gray-50">
  {miscAdminItems.map(item => (
  <tr key={item.id} className="hover:bg-gray-50">
  <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
  {item.first_name} {item.last_name}
  <p className="text-xs text-gray-400 font-normal">{item.borrower_email}</p>
  </td>
  <td className="px-4 py-3 text-gray-700 max-w-[200px]">
  <p className="font-medium text-sm">{item.document_name}</p>
  {item.description && <p className="text-xs text-gray-400 mt-0.5 leading-snug">{item.description}</p>}
  </td>
  <td className="px-4 py-3 text-xs text-gray-500">{item.document_type?.replace(/_/g, ' ')}</td>
  <td className="px-4 py-3">
  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${item.required ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
  {item.required ? 'Required' : 'Optional'}
  </span>
  </td>
  <td className="px-4 py-3">
  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${item.status === 'uploaded' || item.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
  {item.status}
  </span>
  </td>
  <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}</td>
  <td className="px-4 py-3">
  <button
  onClick={async () => {
  await fetch(`${API}/api/documents/admin/miscellaneous/${item.id}`, { method: 'DELETE', headers: authHeaders(token) });
  setMiscAdminItems(prev => prev.filter(i => i.id !== item.id));
  }}
  className="text-xs text-red-500 hover:underline">
  Delete
  </button>
  </td>
  </tr>
  ))}
  </tbody>
  </table>
  </div>
  )}
  </div>
  </div>
  )}

  </div>
  );
}

function AssetSourcesAdminTab({ token }) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
  fetch(`${API}/api/documents/asset-sources/admin`, { headers: authHeaders(token) })
  .then(r => r.json())
  .then(d => { setSources(d.sources || []); })
  .catch(() => {})
  .finally(() => setLoading(false));
  }, [token]);

  const SOURCE_LABELS = { personal: 'Personal Funds', gift: 'Gift Funds', sale: 'Home Sale Proceeds', retirement: 'Retirement Account(s)', investment: 'Investment Account(s)' };
  const statusColor = (s) => s === 'complete' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700';

  if (loading) return <div className="flex items-center justify-center py-12"><Spinner /></div>;

  // Group sources by user
  const byUser = sources.reduce((acc, s) => {
  const key = s.user_id;
  if (!acc[key]) acc[key] = { name: `${s.first_name} ${s.last_name}`, email: s.email, sources: [] };
  acc[key].sources.push(s);
  return acc;
  }, {});

  return (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
  <div className="px-5 py-4 border-b border-gray-50">
  <h3 className="font-semibold text-gray-800">Asset Sources</h3>
  <p className="text-xs text-gray-400 mt-0.5">All borrower-declared asset sources and their document status</p>
  </div>
  {Object.keys(byUser).length === 0 ? (
  <div className="text-center py-12 text-gray-400">No asset sources declared yet.</div>
  ) : (
  <div className="divide-y divide-gray-50">
  {Object.values(byUser).map((borrower, i) => (
  <div key={i} className="px-5 py-4">
  <div className="flex items-center gap-2 mb-3">
  <span className="font-semibold text-gray-900 text-sm">{borrower.name}</span>
  <span className="text-xs text-gray-400">{borrower.email}</span>
  </div>
  <div className="space-y-2">
  {borrower.sources.map(s => (
  <div key={s.id} className="flex items-center gap-3 text-xs">
  <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center flex-shrink-0">{s.source_index}</span>
  <span className="font-medium text-gray-700 w-40">{SOURCE_LABELS[s.source_type] || s.source_type || '—'}</span>
  <span className={`px-2 py-0.5 rounded-full font-semibold ${statusColor(s.status)}`}>{s.status}</span>
  <span className="text-gray-400">{s.doc_count} doc{s.doc_count !== 1 ? 's' : ''} uploaded</span>
  <span className="text-gray-300 ml-auto">{s.created_at ? new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
  </div>
  ))}
  </div>
  </div>
  ))}
  </div>
  )}
  </div>
  );
}

function SettingsSection({ token }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [profileForm, setProfileForm] = useState({ first_name: '', last_name: '', email: '', current_password: '', new_password: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
  fetch(`${API}/api/admin/settings`, { headers: authHeaders(token) })
  .then(r => r.json()).then(d => {
  setData(d);
  const s = d.settings || {};
  setForm({
  available_states: (JSON.parse(s.available_states || '[]')).join(', '),
  });
  const p = d.adminProfile || {};
  setProfileForm(prev => ({ ...prev, first_name: p.first_name || '', last_name: p.last_name || '', email: p.email || '' }));
  });
  }, [token]);

  const save = async () => {
  setSaving(true);
  setMsg('');
  const settingsPayload = {
  available_states: JSON.stringify(form.available_states.split(',').map(s => s.trim()).filter(Boolean)),
  };
  const r = await fetch(`${API}/api/admin/settings`, {
  method: 'PUT', headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
  body: JSON.stringify({ settings: settingsPayload, profile: profileForm }),
  });
  const d = await r.json();
  setSaving(false);
  setMsg(d.error || 'Settings saved.');
  };

  if (!data) return <div className="flex justify-center py-20"><Spinner size={8} /></div>;

  return (
  <div className="max-w-2xl space-y-6">
  {msg && <div className={`rounded-xl px-4 py-3 text-sm ${msg.includes('error') || msg.includes('Error') ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>{msg}</div>}

  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
  <h3 className="font-semibold text-gray-800">Admin Profile</h3>
  <div className="grid grid-cols-2 gap-3">
  {[['first_name', 'First Name'], ['last_name', 'Last Name']].map(([k, l]) => (
  <div key={k}>
  <label className="block text-xs font-medium text-gray-500 mb-1">{l}</label>
  <input value={profileForm[k]} onChange={e => setProfileForm(f => ({ ...f, [k]: e.target.value }))}
  className="w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
  </div>
  ))}
  </div>
  <div>
  <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
  <input type="email" value={profileForm.email} onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))}
  className="w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
  </div>
  <div className="grid grid-cols-2 gap-3">
  <div>
  <label className="block text-xs font-medium text-gray-500 mb-1">Current Password</label>
  <input type="password" value={profileForm.current_password} onChange={e => setProfileForm(f => ({ ...f, current_password: e.target.value }))}
  placeholder="Leave blank to keep" className="w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
  </div>
  <div>
  <label className="block text-xs font-medium text-gray-500 mb-1">New Password</label>
  <input type="password" value={profileForm.new_password} onChange={e => setProfileForm(f => ({ ...f, new_password: e.target.value }))}
  placeholder="Leave blank to keep" className="w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
  </div>
  </div>
  </div>

  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
  <h3 className="font-semibold text-gray-800">Platform Settings</h3>
  <div>
  <label className="block text-xs font-medium text-gray-500 mb-1">Available States (comma-separated)</label>
  <input value={form.available_states} onChange={e => setForm(f => ({ ...f, available_states: e.target.value }))}
  placeholder="TX, FL, CA" className="w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
  </div>
  <div className="space-y-3">
  {[
  ].map(([k, l]) => (
  <label key={k} className="flex items-center gap-3 cursor-pointer">
  <button type="button" onClick={() => setForm(f => ({ ...f, [k]: !f[k] }))}
  className={`w-10 h-5 rounded-full transition-colors relative ${form[k] ? 'bg-blue-500' : 'bg-gray-200'}`}>
  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form[k] ? 'translate-x-5' : 'translate-x-0.5'}`} />
  </button>
  <span className="text-sm text-gray-700">{l}</span>
  </label>
  ))}
  </div>
  </div>

  <button onClick={save} disabled={saving}
  className="btn-primary text-white disabled:opacity-50"
  style={{ backgroundColor: BLUE }}>
  {saving ? 'Saving…' : (
  <>
  <span>Save All Settings</span>
  <span className="btn-arrow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg></span>
  </>
  )}
  </button>
  </div>
  );
}

// ── SmartEstimate Sub-Tab ─────────────────────────────────────────────────────
function SmartEstimateTab({ token }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  // 🔑 No max_approval_amount here (fp-maxappr decision 4, Jesse 2026-11-02): the maximum is the live decision's, shown
  // read-only (decision_max from GET /api/admin/users); the decision path owns it.
  const [form, setForm] = useState({ approved_interest_rate: '', smartestimate_accepted: false });
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    fetch(`${API}/api/admin/users?limit=100`, { headers: authHeaders(token) })
      .then((r) => r.json())
      .then((d) => {
        const withApps = (d.users || []).filter((u) => u.app_count > 0 || u.application_status === 'submitted');
        setUsers(withApps);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const openEdit = (u) => {
    setEditId(u.id);
    setForm({
      approved_interest_rate: u.approved_interest_rate || '',
      smartestimate_accepted: !!u.smartestimate_accepted,
    });
    setSavedMsg('');
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      approved_interest_rate: form.approved_interest_rate ? Number(form.approved_interest_rate) : undefined,
      smartestimate_accepted: form.smartestimate_accepted,
    };
    await fetch(`${API}/api/admin/users/${editId}/smartestimate`, {
      method: 'PUT',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    setUsers((prev) => prev.map((u) =>
      u.id === editId
        ? { ...u, approved_interest_rate: payload.approved_interest_rate, smartestimate_accepted: payload.smartestimate_accepted ? 1 : 0 }
        : u
    ));
    setSaving(false);
    setSavedMsg('Saved!');
    setTimeout(() => setSavedMsg(''), 2000);
  };

  if (loading) return <div className="flex justify-center py-16"><Spinner size={8} /></div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">Set interest rates for borrowers who have submitted applications. The maximum comes from the borrower's live decision and is not edited here. Marking a borrower as accepted unlocks the Homes page for them.</p>

      {users.length === 0 ? (
        <EmptyState message="No borrowers with submitted applications yet." />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <TH>Borrower</TH>
                <TH>App Status</TH>
                <TH>Max Approval</TH>
                <TH>Rate</TH>
                <TH>SmartEstimate™</TH>
                <TH>Action</TH>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {users.map((u) => (
                <React.Fragment key={u.id}>
                  <tr className="hover:bg-gray-50/50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{u.first_name} {u.last_name}</p>
                      <p className="text-xs text-gray-400">{u.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                        u.application_status === 'submitted' ? 'bg-green-100 text-green-700' :
                        u.application_status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {u.application_status || 'not_started'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 font-medium">
                      {Number(u.decision_max) > 0 ? fmt$(u.decision_max) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {u.approved_interest_rate ? formatRate(u.approved_interest_rate) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                        u.smartestimate_accepted ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {u.smartestimate_accepted ? '✓ Active' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => editId === u.id ? setEditId(null) : openEdit(u)}
                        className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                      >
                        {editId === u.id ? 'Cancel' : 'Edit'}
                      </button>
                    </td>
                  </tr>
                  {editId === u.id && (
                    <tr className="bg-blue-50/50">
                      <td colSpan={6} className="px-4 py-4">
                        <div className="flex flex-wrap gap-4 items-end">
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">Interest Rate (%)</label>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="e.g. 6.75"
                              value={form.approved_interest_rate}
                              onChange={(e) => setForm((f) => ({ ...f, approved_interest_rate: e.target.value }))}
                              className="h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 w-32"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">Mark as Accepted</label>
                            <button
                              type="button"
                              onClick={() => setForm((f) => ({ ...f, smartestimate_accepted: !f.smartestimate_accepted }))}
                              className={`w-12 h-6 rounded-full transition-colors relative ${form.smartestimate_accepted ? 'bg-green-500' : 'bg-gray-200'}`}
                            >
                              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.smartestimate_accepted ? 'translate-x-7' : 'translate-x-1'}`} />
                            </button>
                          </div>
                          <button
                            onClick={handleSave}
                            disabled={saving}
                            className="h-9 px-4 rounded-lg text-sm font-semibold text-white disabled:opacity-50 transition-opacity"
                            style={{ backgroundColor: BLUE }}
                          >
                            {saving ? 'Saving…' : 'Save'}
                          </button>
                          {savedMsg && <span className="text-xs text-green-600 font-semibold">{savedMsg}</span>}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Verification Methods Sub-Tab ──────────────────────────────────────────────
function VerificationMethodsTab({ token }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
  fetch(`${API}/api/admin/users?limit=100`, { headers: authHeaders(token) })
  .then((r) => r.json())
  .then((data) => {
  const withApp = (data.users || []).filter((u) => u.app_count > 0);
  setUsers(withApp);
  })
  .catch(() => {})
  .finally(() => setLoading(false));
  }, [token]);

  const toggle = async (uid, field, current) => {
  setSaving((p) => ({ ...p, [`${uid}_${field}`]: true }));
  try {
  await fetch(`${API}/api/admin/users/${uid}/verification`, {
  method: 'PUT',
  headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
  body: JSON.stringify({ field, value: !current }),
  });
  setUsers((prev) => prev.map((u) => {
  if (u.id !== uid) return u;
  const updated = { ...u };
  if (field === 'argyle') updated.argyle_connected = current ? 0 : 1;
  if (field === 'plaid_income') updated.plaid_income_connected = current ? 0 : 1;
  if (field === 'plaid_assets') updated.plaid_assets_connected = current ? 0 : 1;
  return updated;
  }));
  } catch (_) {}
  setSaving((p) => ({ ...p, [`${uid}_${field}`]: false }));
  };

  const empLabel = (type) => {
  const map = { w2: 'W-2', self_employed: 'Self-Employed', '1099': '1099', retired: 'Retired', military: 'Military' };
  return map[(type || '').toLowerCase().replace(/[- ]/g, '_')] || type || '—';
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Spinner /></div>;

  return (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
  {users.length === 0 ? (
  <div className="text-center py-12 text-gray-400">No borrowers with applications yet.</div>
  ) : (
  <div className="overflow-x-auto">
  <table className="w-full text-sm">
  <thead className="bg-gray-50 border-b border-gray-100">
  <tr>
  {['Borrower', 'Income Type', 'Argyle', 'Plaid Income', 'Plaid Assets', 'Status'].map((h) => (
  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
  ))}
  </tr>
  </thead>
  <tbody className="divide-y divide-gray-50">
  {users.map((u) => {
  const argyle = !!u.argyle_connected;
  const plaidIncome = !!u.plaid_income_connected;
  const plaidAssets = !!u.plaid_assets_connected;
  const incomeVerified = argyle || plaidIncome;
  const Toggle = ({ field, value }) => (
  <button
  onClick={() => toggle(u.id, field, value)}
  disabled={!!saving[`${u.id}_${field}`]}
  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${value ? 'bg-green-500' : 'bg-gray-200'}`}
  >
  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${value ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
  </button>
  );
  return (
  <tr key={u.id} className="hover:bg-gray-50/50">
  <td className="px-4 py-3">
  <div className="font-medium text-gray-900">{u.first_name} {u.last_name}</div>
  <div className="text-xs text-gray-400">{u.email}</div>
  </td>
  <td className="px-4 py-3 text-gray-600">{empLabel(u.employment_type || '')}</td>
  <td className="px-4 py-3"><Toggle field="argyle" value={argyle} /></td>
  <td className="px-4 py-3"><Toggle field="plaid_income" value={plaidIncome} /></td>
  <td className="px-4 py-3"><Toggle field="plaid_assets" value={plaidAssets} /></td>
  <td className="px-4 py-3">
  {incomeVerified
  ? <span className="px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-700 rounded-full">Income Verified</span>
  : <span className="px-2 py-0.5 text-xs font-semibold bg-gray-100 text-gray-500 rounded-full">Not Verified</span>}
  </td>
  </tr>
  );
  })}
  </tbody>
  </table>
  </div>
  )}
  </div>
  );
}

// Offer-request stage badge maps (module-scope, mirrors L29 STATUS_COLORS
// pattern). Drives the per-card stage pill in the Homes → Offers panel.
// option_period and downstream are bucketed as "Completed" — once the
// borrower is past offer acceptance, the offer request itself is done
// and the rest of the lifecycle lives on the loan side.
const OFFER_STAGE_LABELS = {
  offer_requested: 'Offer Requested',
  offer_submitted: 'Offer Submitted',
  offer_accepted:  'Offer Accepted',
  under_contract:  'Under Contract',
  option_period:   'Completed',
  clear_to_close:  'Completed',
  closing:         'Completed',
  closed:          'Completed',
};
// Solid pills matching the secondary-palette scheme in constants/statusColors.js
// (bg = main color, text = black/white per contrast, border = bg).
const OFFER_STAGE_COLORS = {
  offer_requested: { bg: '#FF7A1A', text: '#1a1a1a', border: '#FF7A1A' }, // Orange
  offer_submitted: { bg: '#FF6B9D', text: '#1a1a1a', border: '#FF6B9D' }, // Coral
  offer_accepted:  { bg: '#D946C7', text: '#ffffff', border: '#D946C7' }, // Magenta
  under_contract:  { bg: '#14B8A6', text: '#1a1a1a', border: '#14B8A6' }, // Teal
  option_period:   { bg: '#FFDD86', text: '#1a1a1a', border: '#FFDD86' }, // Tan
  clear_to_close:  { bg: '#4F46E5', text: '#ffffff', border: '#4F46E5' }, // Indigo
  closing:         { bg: '#14B8A6', text: '#1a1a1a', border: '#14B8A6' }, // Teal
  closed:          { bg: '#111827', text: '#ffffff', border: '#111827' }, // Dark (semantic)
};

// ── HomesSection ─────────────────────────────────────────────────────────────
function HomesSection({ token }) {
  const navigate = useNavigate();
  const [subTab, setSubTab] = useState('showings');
  const [showings, setShowings] = useState([]);
  const [offers, setOffers] = useState([]);
  const [savedStats, setSavedStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
  setLoading(true);
  Promise.all([
  fetch(`${API}/api/homes/admin/showings`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()),
  fetch(`${API}/api/homes/admin/offers`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()),
  fetch(`${API}/api/homes/admin/saved-stats`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()),
  ])
  .then(([s, o, sv]) => {
  setShowings(s.showings || []);
  setOffers(o.offers || []);
  setSavedStats(sv.stats || []);
  })
  .catch(() => {})
  .finally(() => setLoading(false));
  }, [token]);

  const updateShowingStatus = async (id, status) => {
  await fetch(`${API}/api/homes/admin/showings/${id}`, {
  method: 'PUT',
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  body: JSON.stringify({ status }),
  });
  setShowings((prev) => prev.map((s) => s.id === id ? { ...s, status } : s));
  };

  const SHOWING_STATUSES = ['pending', 'confirmed', 'completed', 'cancelled'];

  const statusColor = (s) => {
  if (s === 'pending') return 'bg-yellow-100 text-yellow-700';
  if (s === 'confirmed' || s === 'submitted') return 'bg-green-100 text-green-700';
  if (s === 'completed') return 'bg-blue-100 text-blue-700';
  if (s === 'cancelled' || s === 'declined') return 'bg-red-100 text-red-700';
  return 'bg-gray-100 text-gray-600';
  };

  return (
  <div>
  <h2 className="text-xl font-bold text-gray-900 mb-1">Homes</h2>
  <p className="text-sm text-gray-500 mb-6">Showing requests, offer requests, and saved home statistics.</p>

  {/* Stats row */}
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
  <StatCard label="Showing Requests" value={showings.length} color="text-blue-600" />
  <StatCard label="Offer Requests" value={offers.length} color="text-green-600" />
  <StatCard label="Most Saved Listing" value={savedStats[0]?.listing_id ? `#${savedStats[0].listing_id} (${savedStats[0].save_count}×)` : ''} />
  </div>

  {/* Sub-tabs */}
  <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-5 w-fit">
  {[
  { id: 'showings', label: `Showings (${showings.length})` },
  { id: 'offers', label: `Offers (${offers.length})` },
  { id: 'saved', label: 'Most Saved' },
  { id: 'smartestimate', label: 'SmartEstimate™' },
  { id: 'verification', label: 'Verification' },
  ].map((t) => (
  <button key={t.id} onClick={() => setSubTab(t.id)}
  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${subTab === t.id ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-black'}`}>
  {t.label}
  </button>
  ))}
  </div>

  {loading ? (
  <div className="flex items-center justify-center py-12"><Spinner /></div>
  ) : subTab === 'showings' ? (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
  {showings.length === 0 ? (
  <div className="text-center py-12 text-gray-400">No showing requests yet.</div>
  ) : (
  <div className="overflow-x-auto">
  <table className="w-full text-sm">
  <thead className="bg-gray-50 border-b border-gray-100">
  <tr>
  {['Borrower', 'Address', 'Date', 'Time', 'Notes', 'Status', 'Action'].map((h) => (
  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
  ))}
  </tr>
  </thead>
  <tbody className="divide-y divide-gray-50">
  {showings.map((s) => (
  <tr key={s.id} className="hover:bg-gray-50/50">
  <td className="px-4 py-3">
  <p className="font-medium text-gray-900">{s.first_name} {s.last_name}</p>
  <p className="text-xs text-gray-400">{s.email}</p>
  </td>
  <td className="px-4 py-3 text-gray-700 max-w-[200px] truncate">{s.address}</td>
  {/* 🚨 THE `capitalize` CLASS WAS A FOSSIL OF THE PERIOD FORMAT. It sat on
      preferred_time because that column once held words — morning, afternoon —
      written by the Homes form's <select>. On a clock time it renders "15:00"
      unchanged and says nothing, while telling the next reader the field holds
      prose. It now applies only where a period is actually shown.

      ⚠️ AND "not sure yet" IS RENDERED AS ITSELF. A blank cell is
      indistinguishable from a dropped value; a borrower who deliberately gave no
      time is a different fact and staff act on it differently. */}
  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
    {s.preferred_date ? fmtDate(s.preferred_date) : <span className="text-gray-400 italic">no date yet</span>}
  </td>
  <td className={`px-4 py-3 text-gray-600${s.preferred_period ? ' capitalize' : ''}`}>
    {s.preferred_period || s.preferred_time || <span className="text-gray-400 italic">to be arranged</span>}
  </td>
  <td className="px-4 py-3 text-gray-500 max-w-[150px] truncate">{s.notes || ''}</td>
  <td className="px-4 py-3">
  <Badge text={s.status} className={statusColor(s.status)} />
  </td>
  <td className="px-4 py-3">
  <select
  value={s.status}
  onChange={(e) => updateShowingStatus(s.id, e.target.value)}
  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300"
  >
  {SHOWING_STATUSES.map((st) => <option key={st} value={st} className="capitalize">{st}</option>)}
  </select>
  </td>
  </tr>
  ))}
  </tbody>
  </table>
  </div>
  )}
  </div>
  ) : subTab === 'offers' ? (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    {offers.length === 0 ? (
      <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: '32px 0' }}>
        No offer requests yet.
      </div>
    ) : offers.map((o) => {
      const stage = o.loan_stage || o.application_status;
      const stageLabel = OFFER_STAGE_LABELS[stage] || 'Pending';
      const stageColors = OFFER_STAGE_COLORS[stage] || { bg: '#f9fafb', text: '#6b7280', border: '#e5e7eb' };
      return (
        <div key={o.id} style={{
          background: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: 10,
          padding: '12px 14px',
        }}>
          {/* Row 1: name + stage badge */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
              {o.first_name} {o.last_name}
            </div>
            <span style={{
              fontSize: 10, fontWeight: 600,
              background: stageColors.bg,
              color: stageColors.text,
              border: `1px solid ${stageColors.border}`,
              borderRadius: 20,
              padding: '2px 8px',
              whiteSpace: 'nowrap',
              marginLeft: 8,
            }}>
              {stageLabel}
            </span>
          </div>

          {/* Row 2: address */}
          {o.address && (
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {o.address}
            </div>
          )}

          {/* Row 3: requested date */}
          {o.created_at && (
            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 10 }}>
              Requested {new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          )}

          {/* Go to Property Tab — AdminBorrowerProfile reads ?tab= at L2895
              and uses 'property_details' as the tab id (per the tabs array
              at L37). */}
          <button
            onClick={() => navigate(`/admin/borrowers/${o.user_id}?tab=property_details`)}
            style={{
              width: '100%',
              background: '#3a6ad9',
              color: '#ffffff',
              border: 'none',
              borderRadius: 7,
              padding: '7px 12px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Go to Property Tab →
          </button>
        </div>
      );
    })}
  </div>
  ) : subTab === 'saved' ? (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
  {savedStats.length === 0 ? (
  <div className="text-center py-12 text-gray-400">No saved homes data yet.</div>
  ) : (
  <div className="overflow-x-auto">
  <table className="w-full text-sm">
  <thead className="bg-gray-50 border-b border-gray-100">
  <tr>
  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Listing ID</th>
  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Times Saved</th>
  </tr>
  </thead>
  <tbody className="divide-y divide-gray-50">
  {savedStats.map((row) => (
  <tr key={row.listing_id} className="hover:bg-gray-50/50">
  <td className="px-4 py-3 font-medium text-gray-900">Listing #{row.listing_id}</td>
  <td className="px-4 py-3">
  <div className="flex items-center gap-2">
  <div className="flex-1 bg-gray-100 rounded-full h-2 max-w-[120px]">
  <div
  className="h-2 rounded-full"
  style={{ backgroundColor: BLUE, width: `${Math.min((row.save_count / (savedStats[0]?.save_count || 1)) * 100, 100)}%` }}
  />
  </div>
  <span className="text-gray-700 font-semibold">{row.save_count}</span>
  </div>
  </td>
  </tr>
  ))}
  </tbody>
  </table>
  </div>
  )}
  </div>
  ) : subTab === 'smartestimate' ? (
  <SmartEstimateTab token={token} />
  ) : (
  <VerificationMethodsTab token={token} />
  )}
  </div>
  );
}

// ── OptionPeriodSection ──────────────────────────────────────────────────────
function OptionPeriodSection({ token }) {
  const [subTab, setSubTab] = useState('periods');
  const [periods, setPeriods] = useState([]);
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(null); // option period record
  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', property_address: '', purchase_price: '', contract_date: '', option_period_days: '10', option_end_date: '' });
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
  setLoading(true);
  try {
  const [pRes, cRes] = await Promise.all([
  fetch(`${API}/api/option-period/admin/all`, { headers: authHeaders(token) }).then(r => r.json()),
  fetch(`${API}/api/option-period/admin/credit-requests`, { headers: authHeaders(token) }).then(r => r.json()),
  ]);
  setPeriods(pRes.records || []);
  setCredits(cRes.requests || []);
  } catch {}
  setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
  e.preventDefault();
  setSaving(true);
  try {
  await fetch(`${API}/api/option-period/admin/create`, {
  method: 'POST',
  headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
  body: JSON.stringify(createForm),
  });
  setCreateModal(false);
  setCreateForm({ email: '', property_address: '', purchase_price: '', contract_date: '', option_period_days: '10', option_end_date: '' });
  load();
  } catch {}
  setSaving(false);
  };

  const handleEdit = async (e) => {
  e.preventDefault();
  setSaving(true);
  try {
  await fetch(`${API}/api/option-period/admin/${editModal.id}`, {
  method: 'PUT',
  headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
  body: JSON.stringify(editForm),
  });
  setEditModal(null);
  load();
  } catch {}
  setSaving(false);
  };

  const updateCredit = async (id, status, admin_notes) => {
  await fetch(`${API}/api/option-period/admin/credit-requests/${id}`, {
  method: 'PUT',
  headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
  body: JSON.stringify({ status, admin_notes }),
  });
  load();
  };

  const statusBadge = (s) => {
  const map = {
  unpaid: 'bg-gray-100 text-gray-600',
  paid: 'bg-yellow-100 text-yellow-700',
  complete: 'bg-green-100 text-green-700',
  pending: 'bg-gray-100 text-gray-600',
  approved: 'bg-green-100 text-green-700',
  denied: 'bg-red-100 text-red-700',
  };
  return <Badge text={s} className={map[s] || 'bg-gray-100 text-gray-600'} />;
  };

  return (
  <div>
  {/* Create Modal */}
  {createModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
  <h3 className="text-lg font-bold mb-4">Create Option Period</h3>
  <form onSubmit={handleCreate} className="space-y-3">
  {[
  ['email', 'Borrower Email', 'email', true],
  ['property_address', 'Property Address', 'text', true],
  ['purchase_price', 'Purchase Price', 'number', true],
  ['contract_date', 'Contract Date', 'date', false],
  ['option_period_days', 'Option Period Days', 'number', false],
  ['option_end_date', 'Option End Date', 'date', false],
  ].map(([field, label, type, required]) => (
  <div key={field}>
  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</label>
  <input
  type={type}
  value={createForm[field]}
  onChange={(e) => setCreateForm(f => ({ ...f, [field]: e.target.value }))}
  required={required}
  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
  />
  </div>
  ))}
  <div className="flex gap-3 pt-2">
  <button type="button" onClick={() => setCreateModal(false)} className="flex-1 border border-gray-200 text-gray-600 rounded-xl py-2 text-sm font-semibold hover:bg-gray-50">Cancel</button>
  <button type="submit" disabled={saving} className="flex-1 text-white rounded-xl py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #3a6ad9, #4f46e5)' }}>
  {saving ? 'Creating…' : 'Create'}
  </button>
  </div>
  </form>
  </div>
  </div>
  )}

  {/* Edit Modal */}
  {editModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
  <h3 className="text-lg font-bold mb-1">Edit Option Period</h3>
  <p className="text-sm text-gray-500 mb-4">{editModal.email}</p>
  <form onSubmit={handleEdit} className="space-y-3">
  {[
  ['property_address', 'Property Address', 'text'],
  ['purchase_price', 'Purchase Price', 'number'],
  ['contract_date', 'Contract Date', 'date'],
  ['option_period_days', 'Option Period Days', 'number'],
  ['option_end_date', 'Option End Date', 'date'],
  ].map(([field, label, type]) => (
  <div key={field}>
  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</label>
  <input
  type={type}
  value={editForm[field] ?? editModal[field] ?? ''}
  onChange={(e) => setEditForm(f => ({ ...f, [field]: e.target.value }))}
  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
  />
  </div>
  ))}
  {[
  ['appraisal_status', 'Appraisal Status', ['unpaid', 'paid', 'complete']],
  ['inspection_status', 'Inspection Status', ['unpaid', 'paid', 'complete']],
  ['report_review_status', 'Report Review', ['pending', 'complete']],
  ].map(([field, label, options]) => (
  <div key={field}>
  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</label>
  <select
  value={editForm[field] ?? editModal[field] ?? ''}
  onChange={(e) => setEditForm(f => ({ ...f, [field]: e.target.value }))}
  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white"
  >
  {options.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1).replace('_', ' ')}</option>)}
  </select>
  </div>
  ))}
  <div>
  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Appraisal Report Notes</label>
  <textarea
  value={editForm.appraisal_report_text ?? editModal.appraisal_report_text ?? ''}
  onChange={(e) => setEditForm(f => ({ ...f, appraisal_report_text: e.target.value }))}
  rows={2}
  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
  />
  </div>
  <div>
  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Inspection Report Notes</label>
  <textarea
  value={editForm.inspection_report_text ?? editModal.inspection_report_text ?? ''}
  onChange={(e) => setEditForm(f => ({ ...f, inspection_report_text: e.target.value }))}
  rows={2}
  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
  />
  </div>
  <div>
  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Admin Notes</label>
  <textarea
  value={editForm.admin_notes ?? editModal.admin_notes ?? ''}
  onChange={(e) => setEditForm(f => ({ ...f, admin_notes: e.target.value }))}
  rows={2}
  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
  />
  </div>
  <div className="flex gap-3 pt-2">
  <button type="button" onClick={() => setEditModal(null)} className="flex-1 border border-gray-200 text-gray-600 rounded-xl py-2 text-sm font-semibold hover:bg-gray-50">Cancel</button>
  <button type="submit" disabled={saving} className="flex-1 text-white rounded-xl py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #3a6ad9, #4f46e5)' }}>
  {saving ? 'Saving…' : 'Save Changes'}
  </button>
  </div>
  </form>
  </div>
  </div>
  )}

  <div className="flex items-center justify-between mb-6">
  <h2 className="text-xl font-bold text-gray-900">Option Period</h2>
  <button
  onClick={() => setCreateModal(true)}
  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-xl hover:opacity-90"
  style={{ background: 'linear-gradient(135deg, #3a6ad9, #4f46e5)' }}
  >
  + Create Option Period
  </button>
  </div>

  {/* Stat cards */}
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
  <StatCard label="Active Periods" value={periods.length} color="text-purple-600" />
  <StatCard label="Credit Requests" value={credits.length} color="text-blue-600" />
  <StatCard label="Pending Credits" value={credits.filter(c => c.status === 'pending').length} color="text-yellow-600" />
  <StatCard label="Decided" value={periods.filter(p => p.decision).length} color="text-green-600" />
  </div>

  {/* Sub-tabs */}
  <div className="flex gap-2 mb-6">
  {[['periods', 'Option Periods'], ['credits', 'Credit Requests']].map(([id, label]) => (
  <button
  key={id}
  onClick={() => setSubTab(id)}
  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
  subTab === id ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
  }`}
  style={subTab === id ? { background: 'linear-gradient(135deg, #3a6ad9, #4f46e5)' } : {}}
  >
  {label}
  </button>
  ))}
  </div>

  {loading ? (
  <div className="flex justify-center py-12"><Spinner size={8} /></div>
  ) : subTab === 'periods' ? (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
  {periods.length === 0 ? (
  <div className="p-8 text-center text-gray-400">No option periods yet. Create one above.</div>
  ) : (
  <div className="overflow-x-auto">
  <table className="w-full text-sm">
  <thead>
  <tr className="border-b border-gray-100 bg-gray-50">
  {['Borrower', 'Property', 'Price', 'Option End', 'Appraisal', 'Inspection', 'Review', 'Decision', ''].map(h => (
  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
  ))}
  </tr>
  </thead>
  <tbody className="divide-y divide-gray-50">
  {periods.map((p) => (
  <tr key={p.id} className="hover:bg-gray-50 transition-colors">
  <td className="px-4 py-3 whitespace-nowrap">
  <p className="font-semibold text-black">{p.first_name} {p.last_name}</p>
  <p className="text-xs text-gray-400">{p.email}</p>
  </td>
  <td className="px-4 py-3 max-w-[180px]">
  <p className="text-xs text-gray-700 truncate">{p.property_address}</p>
  </td>
  <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold">
  ${Number(p.purchase_price).toLocaleString()}
  </td>
  <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
  {fmtDate(p.option_end_date)}
  </td>
  <td className="px-4 py-3 whitespace-nowrap">{statusBadge(p.appraisal_status)}</td>
  <td className="px-4 py-3 whitespace-nowrap">{statusBadge(p.inspection_status)}</td>
  <td className="px-4 py-3 whitespace-nowrap">{statusBadge(p.report_review_status)}</td>
  <td className="px-4 py-3 whitespace-nowrap">
  {p.decision ? statusBadge(p.decision) : <span className="text-gray-400 text-xs"></span>}
  </td>
  <td className="px-4 py-3 whitespace-nowrap">
  <button
  onClick={() => { setEditModal(p); setEditForm({}); }}
  className="text-xs text-purple-600 font-semibold hover:text-purple-800"
  >
  Edit
  </button>
  </td>
  </tr>
  ))}
  </tbody>
  </table>
  </div>
  )}
  </div>
  ) : (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
  {credits.length === 0 ? (
  <div className="p-8 text-center text-gray-400">No credit requests yet.</div>
  ) : (
  <div className="overflow-x-auto">
  <table className="w-full text-sm">
  <thead>
  <tr className="border-b border-gray-100 bg-gray-50">
  {['Borrower', 'Property', 'Amount', 'Reason', 'Notes', 'Status', 'Actions'].map(h => (
  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
  ))}
  </tr>
  </thead>
  <tbody className="divide-y divide-gray-50">
  {credits.map((cr) => (
  <tr key={cr.id} className="hover:bg-gray-50 transition-colors">
  <td className="px-4 py-3 whitespace-nowrap">
  <p className="font-semibold text-black">{cr.first_name} {cr.last_name}</p>
  <p className="text-xs text-gray-400">{cr.email}</p>
  </td>
  <td className="px-4 py-3 max-w-[160px]">
  <p className="text-xs text-gray-700 truncate">{cr.property_address}</p>
  </td>
  <td className="px-4 py-3 whitespace-nowrap font-semibold">${Number(cr.amount).toLocaleString()}</td>
  <td className="px-4 py-3 max-w-[160px]">
  <p className="text-xs text-gray-700 truncate">{cr.reason}</p>
  </td>
  <td className="px-4 py-3 max-w-[140px]">
  <p className="text-xs text-gray-500 truncate">{cr.notes || ''}</p>
  </td>
  <td className="px-4 py-3 whitespace-nowrap">{statusBadge(cr.status)}</td>
  <td className="px-4 py-3 whitespace-nowrap">
  <div className="flex gap-2">
  {cr.status !== 'approved' && (
  <button onClick={() => updateCredit(cr.id, 'approved', cr.admin_notes)} className="text-xs text-green-600 font-semibold hover:text-green-800">Approve</button>
  )}
  {cr.status !== 'denied' && (
  <button onClick={() => updateCredit(cr.id, 'denied', cr.admin_notes)} className="text-xs text-red-600 font-semibold hover:text-red-800">Deny</button>
  )}
  </div>
  </td>
  </tr>
  ))}
  </tbody>
  </table>
  </div>
  )}
  </div>
  )}
  </div>
  );
}

// ── ClosingPipelineSection ────────────────────────────────────────────────────
function ClosingPipelineSection({ token }) {
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [checkUpdating, setCheckUpdating] = useState('');
  const [ctcLoading, setCtcLoading] = useState(false);
  const [fundsLoading, setFundsLoading] = useState(false);
  const [noteInputs, setNoteInputs] = useState({});
  const [successMsg, setSuccessMsg] = useState('');

  const CHECKS = [
  { key: 'appraisal_check', label: 'Appraisal', emoji: '🏠' },
  { key: 'title_check', label: 'Title', emoji: '📋' },
  { key: 'insurance_check', label: 'Insurance', emoji: '🛡️' },
  { key: 'compliance_check',label: 'Compliance', emoji: '✅' },
  { key: 'funds_check', label: 'Funds', emoji: '💰' },
  ];

  const load = useCallback(async () => {
  setLoading(true);
  try {
  const r = await fetch(`${API}/api/final-review/admin/pipeline`, { headers: { Authorization: `Bearer ${token}` } });
  const d = await r.json();
  setPipeline(Array.isArray(d) ? d : []);
  } catch { setPipeline([]); }
  setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 3000); };

  const updateCheck = async (userId, checkName, value) => {
  const notes = noteInputs[`${userId}_${checkName}`] || '';
  setCheckUpdating(`${userId}_${checkName}_${value}`);
  try {
  await fetch(`${API}/api/final-review/admin/${userId}/check`, {
  method: 'PUT',
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  body: JSON.stringify({ checkName, value, notes }),
  });
  flash(`${checkName.replace('_check','').replace('_',' ')} updated to ${value}`);
  await load();
  if (selectedUser?.id === userId) {
  const updated = pipeline.find(u => u.id === userId);
  if (updated) setSelectedUser(updated);
  }
  } catch { /* ignore */ }
  setCheckUpdating('');
  };

  const issueCTC = async (userId) => {
  setCtcLoading(true);
  try {
  await fetch(`${API}/api/final-review/admin/${userId}/ctc`, {
  method: 'PUT',
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  body: JSON.stringify({}),
  });
  flash('Clear to Close issued!');
  await load();
  } catch { /* ignore */ }
  setCtcLoading(false);
  };

  const confirmFunds = async (userId) => {
  setFundsLoading(true);
  try {
  await fetch(`${API}/api/closing/admin/${userId}/funds-received`, {
  method: 'PUT',
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  body: JSON.stringify({}),
  });
  flash('Funds confirmed received');
  await load();
  } catch { /* ignore */ }
  setFundsLoading(false);
  };

  const stageBadge = (stage) => {
  const colors = {
  loan_docs: 'bg-blue-100 text-blue-700',
  closing_disclosures: 'bg-purple-100 text-purple-700',
  final_review: 'bg-indigo-100 text-indigo-700',
  closing: 'bg-yellow-100 text-yellow-700',
  };
  return (
  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors[stage] || 'bg-gray-100 text-gray-600'}`}>
  {stage?.replace(/_/g, ' ') || ''}
  </span>
  );
  };

  const checkPill = (value) => {
  if (value === 'pass') return <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Pass</span>;
  if (value === 'fail') return <span className="text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">Fail</span>;
  return <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">Pending</span>;
  };

  const boolPill = (v) => v
  ? <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">✓</span>
  : <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full"></span>;

  if (loading) return <div className="flex justify-center py-16"><Spinner size={7} /></div>;

  return (
  <div className="space-y-6">
  {/* Header */}
  <div className="flex items-center justify-between">
  <div>
  <h2 className="text-xl font-bold text-gray-900">Closing Pipeline</h2>
  <p className="text-sm text-gray-500 mt-0.5">Borrowers in Loan Docs, Closing Disclosures, and Final Review stages</p>
  </div>
  <button onClick={load} className="text-sm text-blue-600 hover:text-blue-800 font-medium">↻ Refresh</button>
  </div>

  {successMsg && (
  <div className="bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 rounded-xl">{successMsg}</div>
  )}

  {/* Stats row */}
  <div className="grid grid-cols-3 gap-4">
  <StatCard label="In Loan Docs" value={pipeline.filter(u => u.loan_stage === 'loan_docs').length} color="text-blue-600" />
  <StatCard label="In Disclosures" value={pipeline.filter(u => u.loan_stage === 'closing_disclosures').length} color="text-purple-600" />
  <StatCard label="Final Review" value={pipeline.filter(u => u.loan_stage === 'final_review').length} color="text-indigo-600" />
  </div>

  {pipeline.length === 0 ? (
  <div className="text-center py-16 text-gray-400">No borrowers in the closing pipeline.</div>
  ) : (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
  {pipeline.map((u) => (
  <div
  key={u.id}
  className={`bg-white rounded-2xl border shadow-sm p-5 cursor-pointer transition-all ${selectedUser?.id === u.id ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'}`}
  onClick={() => setSelectedUser(selectedUser?.id === u.id ? null : u)}
  >
  {/* Top row */}
  <div className="flex items-start justify-between mb-3">
  <div>
  <p className="font-semibold text-gray-900">{u.first_name} {u.last_name}</p>
  <p className="text-xs text-gray-400">{u.email}</p>
  {u.property_address && <p className="text-xs text-gray-500 mt-0.5">🏠 {u.property_address}</p>}
  </div>
  <div className="flex flex-col items-end gap-1">
  {stageBadge(u.loan_stage)}
  {u.clear_to_close ? <span className="text-xs font-bold text-green-600">🎉 CTC Issued</span> : null}
  </div>
  </div>

  {/* Quick status row */}
  <div className="grid grid-cols-3 gap-2 text-xs">
  <div className="bg-gray-50 rounded-lg p-2">
  <p className="text-gray-400 mb-1">LE Signed</p>
  {boolPill(u.le_signed)}
  </div>
  <div className="bg-gray-50 rounded-lg p-2">
  <p className="text-gray-400 mb-1">Contract</p>
  {boolPill(u.contract_signed)}
  </div>
  <div className="bg-gray-50 rounded-lg p-2">
  <p className="text-gray-400 mb-1">Insurance</p>
  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${u.insurance_status === 'bound' || u.insurance_status === 'verified' ? 'bg-green-100 text-green-700' : u.insurance_status === 'uploaded' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500'}`}>
  {u.insurance_status || 'not started'}
  </span>
  </div>
  <div className="bg-gray-50 rounded-lg p-2">
  <p className="text-gray-400 mb-1">CD Signed</p>
  {boolPill(u.cd_signed)}
  </div>
  <div className="bg-gray-50 rounded-lg p-2">
  <p className="text-gray-400 mb-1">Wire Sent</p>
  {boolPill(u.wire_sent)}
  </div>
  <div className="bg-gray-50 rounded-lg p-2">
  <p className="text-gray-400 mb-1">Funds Rcvd</p>
  {boolPill(u.funds_received)}
  </div>
  </div>

  {/* Expanded detail panel */}
  {selectedUser?.id === u.id && (
  <div className="mt-4 pt-4 border-t border-gray-100 space-y-4" onClick={e => e.stopPropagation()}>
  {/* System checks */}
  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">System Checks</p>
  <div className="space-y-2">
  {CHECKS.map((check) => (
  <div key={check.key} className="flex items-center gap-3">
  <span className="text-base">{check.emoji}</span>
  <span className="text-sm text-gray-700 flex-1">{check.label}</span>
  {checkPill(u[check.key])}
  <div className="flex gap-1">
  <button
  onClick={() => updateCheck(u.id, check.key, 'pass')}
  disabled={checkUpdating !== ''}
  className="text-xs px-2 py-1 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50 font-medium"
  >Pass</button>
  <button
  onClick={() => updateCheck(u.id, check.key, 'fail')}
  disabled={checkUpdating !== ''}
  className="text-xs px-2 py-1 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50 font-medium"
  >Fail</button>
  <button
  onClick={() => updateCheck(u.id, check.key, 'pending')}
  disabled={checkUpdating !== ''}
  className="text-xs px-2 py-1 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 font-medium"
  >Reset</button>
  </div>
  </div>
  ))}
  </div>

  {/* Notes input */}
  <div>
  <label className="block text-xs font-medium text-gray-500 mb-1">Check notes (applied to next update)</label>
  <input
  type="text"
  placeholder="Add a note..."
  value={Object.values(noteInputs).find((_,i) => Object.keys(noteInputs)[i].startsWith(String(u.id))) || ''}
  onChange={e => setNoteInputs(p => ({ ...p, [`${u.id}_note`]: e.target.value }))}
  className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm text-gray-800 outline-none focus:border-blue-400"
  />
  </div>

  {/* Admin actions */}
  <div className="flex flex-wrap gap-2 pt-2">
  {!u.funds_received && (
  <button
  onClick={() => confirmFunds(u.id)}
  disabled={fundsLoading}
  className="text-sm px-4 py-2 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50"
  >
  {fundsLoading ? 'Confirming…' : '💰 Confirm Funds Received'}
  </button>
  )}
  {!u.clear_to_close && (
  <button
  onClick={() => issueCTC(u.id)}
  disabled={ctcLoading}
  className="text-sm px-4 py-2 rounded-xl font-semibold text-white hover:opacity-90 disabled:opacity-50"
  style={{ backgroundColor: '#3a6ad9' }}
  >
  {ctcLoading ? 'Issuing…' : '🎉 Issue Clear to Close'}
  </button>
  )}
  {u.clear_to_close && (
  <span className="text-sm font-semibold text-green-600 bg-green-50 border border-green-200 px-4 py-2 rounded-xl">
  🎉 Clear to Close Issued
  </span>
  )}
  </div>

  {/* Closing appointment */}
  {u.closing_date && (
  <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
  <p className="text-xs font-bold text-blue-700 mb-1">Closing Appointment</p>
  <p className="text-sm text-blue-900">{u.closing_date} at {u.closing_time}</p>
  <p className="text-xs text-blue-600 mt-0.5">{u.closing_location}</p>
  </div>
  )}
  </div>
  )}
  </div>
  ))}
  </div>
  )}
  </div>
  );
}

// ── SurveysAdminPanel ────────────────────────────────────────────────────────
function SurveysAdminPanel({ token }) {
  const [metrics, setMetrics] = useState(null);
  const [responses, setResponses] = useState([]);
  const [subTab, setSubTab] = useState('overview');
  const [expanded, setExpanded] = useState(null);
  const [filterTrigger, setFilterTrigger] = useState('');
  const [filterHasTestimonial, setFilterHasTestimonial] = useState(false);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    fetch(`${API}/api/surveys/admin/metrics`, { headers })
      .then(r => r.json()).then(d => setMetrics(d)).catch(() => {});
    fetch(`${API}/api/surveys/admin/all`, { headers })
      .then(r => r.json()).then(d => setResponses(d.responses || [])).catch(() => {});
  }, [token]);

  const filtered = responses.filter(r => {
    if (filterTrigger && r.trigger_type !== filterTrigger) return false;
    if (filterHasTestimonial && !r.testimonial_text) return false;
    return true;
  });

  const triggerLabels = {
    post_application: 'Post Application',
    post_document_upload: 'Post Document Upload',
    post_approval: 'Post Approval',
    post_home_shopping: 'Post Home Shopping',
    post_option_period: 'Post Option Period',
    post_closing: 'Post Closing',
    passive_feedback: 'Passive Feedback',
  };

  const exportCsv = () => {
    const header = ['Name', 'Email', 'Trigger', 'Date', 'Stars', 'NPS', 'Recommend', 'Testimonial', 'Trustpilot Clicked'];
    const rows = responses.map(r => [
      `${r.first_name || ''} ${r.last_name || ''}`.trim(),
      r.email || '',
      r.trigger_type,
      r.created_at,
      r.star_rating || '',
      r.nps_score || '',
      r.would_recommend != null ? (r.would_recommend ? 'Yes' : 'No') : '',
      (r.testimonial_text || '').replace(/,/g, ';'),
      r.trustpilot_clicked ? 'Yes' : 'No',
    ]);
    const csv = [header, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'survey_responses.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      {/* Sub-tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {[['overview', 'Overview'], ['responses', 'Responses'], ['testimonials', 'Testimonials']].map(([id, label]) => (
          <button key={id} onClick={() => setSubTab(id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${subTab === id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>
            {label}
          </button>
        ))}
      </div>

      {subTab === 'overview' && metrics && (
        <div className="space-y-4">
          {/* Metric cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Surveys Completed', value: metrics.total_completed },
              { label: 'Avg NPS', value: metrics.avg_nps != null ? metrics.avg_nps.toFixed(1) : '—' },
              { label: 'Avg Star Rating', value: metrics.avg_star != null ? `${metrics.avg_star.toFixed(1)} / 5` : '—' },
              { label: 'Trustpilot Clicks', value: metrics.trustpilot_clicks },
            ].map(({ label, value }) => (
              <div key={label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                <p className="text-xs text-gray-400 mb-1">{label}</p>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
              </div>
            ))}
          </div>

          {/* By trigger */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h4 className="font-semibold text-gray-800 mb-3">By Trigger Type</h4>
            {(metrics.by_trigger || []).map(t => (
              <div key={t.trigger_type} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <span className="text-sm text-gray-600">{triggerLabels[t.trigger_type] || t.trigger_type}</span>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-gray-400">{t.count} responses</span>
                  {t.avg_star && <span className="text-xs font-medium text-amber-600">★ {Number(t.avg_star).toFixed(1)}</span>}
                  {t.avg_nps && <span className="text-xs font-medium text-blue-600">NPS {Number(t.avg_nps).toFixed(1)}</span>}
                </div>
              </div>
            ))}
            {(!metrics.by_trigger || metrics.by_trigger.length === 0) && (
              <p className="text-sm text-gray-400">No survey responses yet.</p>
            )}
          </div>

          {/* Star distribution */}
          {metrics.star_distribution?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <h4 className="font-semibold text-gray-800 mb-3">Star Rating Distribution</h4>
              {[5,4,3,2,1].map(star => {
                const found = metrics.star_distribution.find(s => s.star_rating === star);
                const count = found?.count || 0;
                const max = Math.max(...(metrics.star_distribution.map(s => s.count)), 1);
                return (
                  <div key={star} className="flex items-center gap-3 mb-2">
                    <span className="text-xs text-amber-500 w-4">{star}★</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-2">
                      <div className="bg-amber-400 h-2 rounded-full" style={{ width: `${(count / max) * 100}%` }} />
                    </div>
                    <span className="text-xs text-gray-400 w-4">{count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {subTab === 'responses' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Filters + Export */}
          <div className="flex items-center gap-3 p-4 border-b border-gray-50 flex-wrap">
            <select
              value={filterTrigger}
              onChange={e => setFilterTrigger(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5"
            >
              <option value="">All Triggers</option>
              {Object.entries(triggerLabels).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input type="checkbox" checked={filterHasTestimonial} onChange={e => setFilterHasTestimonial(e.target.checked)} />
              Has Testimonial
            </label>
            <button onClick={exportCsv} className="ml-auto text-sm font-medium text-blue-600 hover:text-blue-800">
              Export CSV
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Borrower', 'Trigger', 'Date', 'Stars', 'NPS', 'Recommend', 'Trustpilot', 'Testimonial'].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <React.Fragment key={r.id}>
                    <tr
                      className="border-t border-gray-50 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 text-xs">{r.first_name} {r.last_name}</p>
                        <p className="text-gray-400 text-xs">{r.email}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">{triggerLabels[r.trigger_type] || r.trigger_type}</td>
                      <td className="px-4 py-3 text-xs text-gray-400">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                      <td className="px-4 py-3 text-xs">{r.star_rating ? `${'★'.repeat(r.star_rating)}` : '—'}</td>
                      <td className="px-4 py-3 text-xs font-semibold" style={{ color: r.nps_score >= 9 ? '#acf44e' : r.nps_score >= 7 ? '#ca8a04' : r.nps_score != null ? '#ff082f' : '#9ca3af' }}>{r.nps_score ?? '—'}</td>
                      <td className="px-4 py-3 text-xs">{r.would_recommend != null ? (r.would_recommend ? '✅ Yes' : '❌ No') : '—'}</td>
                      <td className="px-4 py-3 text-xs">{r.trustpilot_clicked ? '✅' : '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 max-w-[150px] truncate">{r.testimonial_text || '—'}</td>
                    </tr>
                    {expanded === r.id && r.testimonial_text && (
                      <tr className="bg-blue-50">
                        <td colSpan={8} className="px-4 py-3 text-xs text-gray-700 leading-relaxed">
                          <strong>Testimonial:</strong> {r.testimonial_text}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-400">No survey responses yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {subTab === 'testimonials' && (
        <div className="space-y-3">
          {responses.filter(r => r.testimonial_text).map(r => (
            <div key={r.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{r.first_name} {r.last_name}</p>
                  <p className="text-xs text-gray-400">{triggerLabels[r.trigger_type] || r.trigger_type} · {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</p>
                </div>
                <div className="flex items-center gap-2">
                  {r.star_rating && <span className="text-amber-400 text-sm">{'★'.repeat(r.star_rating)}</span>}
                  <button
                    onClick={() => navigator.clipboard?.writeText(r.testimonial_text)}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >Copy</button>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed italic">"{r.testimonial_text}"</p>
            </div>
          ))}
          {responses.filter(r => r.testimonial_text).length === 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
              <p className="text-sm text-gray-400">No testimonials yet.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Nav items ────────────────────────────────────────────────────────────────
const NAV = [
  { id: 'overview', label: 'Overview', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { id: 'leads', label: 'Applications', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
  { id: 'applications', label: 'Drafts', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { id: 'pipeline', label: 'Pipeline', icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2' },
  { id: 'waitlist', label: 'Waitlist', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
  { id: 'users', label: 'Users', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
  { id: 'documents', label: 'Documents', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { id: 'homes', label: 'Homes', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { id: 'option_period', label: 'Option Period', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
  { id: 'closing_pipeline', label: 'Closing Pipeline', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { id: 'settings', label: 'Settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' },
];

// ── Main AdminDashboard ──────────────────────────────────────────────────────
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem('doorly_admin_token'));
  const [adminUser, setAdminUser] = useState(null);
  const [section, setSection] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Auth guard
  useEffect(() => {
  const t = localStorage.getItem('doorly_admin_token');
  if (!t) { navigate('/admin/login', { replace: true }); return; }
  const payload = decodeJwt(t);
  if (!payload || !payload.isAdmin || payload.exp * 1000 < Date.now()) {
  localStorage.removeItem('doorly_admin_token');
  navigate('/admin/login', { replace: true });
  return;
  }
  setToken(t);
  const u = localStorage.getItem('doorly_admin_user');
  if (u) { try { setAdminUser(JSON.parse(u)); } catch {} }
  if (localStorage.getItem('doorly_admin_force_pw') === '1') setChangePwOpen(true);
  }, [navigate]);

  // Auto-refresh every 60s
  useEffect(() => {
  const iv = setInterval(() => setRefreshKey(k => k + 1), 60000);
  return () => clearInterval(iv);
  }, []);

  const logout = () => {
  ['doorly_admin_token', 'doorly_admin_user', 'doorly_admin_force_pw'].forEach(k => localStorage.removeItem(k));
  navigate('/admin/login', { replace: true });
  };

  const submitChangePw = async (e) => {
  e.preventDefault();
  setPwError('');
  if (pwForm.next !== pwForm.confirm) { setPwError('Passwords do not match.'); return; }
  if (pwForm.next.length < 8) { setPwError('Password must be at least 8 characters.'); return; }
  setPwSaving(true);
  const r = await fetch(`${API}/api/admin/change-password`, {
  method: 'PUT', headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
  body: JSON.stringify({ currentPassword: pwForm.current, newPassword: pwForm.next }),
  });
  const d = await r.json();
  setPwSaving(false);
  if (!r.ok) { setPwError(d.error || 'Failed to change password.'); return; }
  localStorage.removeItem('doorly_admin_force_pw');
  setChangePwOpen(false);
  setPwForm({ current: '', next: '', confirm: '' });
  };

  if (!token) return null;

  const sectionProps = { token, key: `${section}-${refreshKey}` };

  return (
  <div className="min-h-screen bg-gray-50 flex">
  {/* Sidebar overlay (mobile) */}
  {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

  {/* Sidebar */}
  <aside className={`fixed top-0 left-0 h-full w-56 bg-white border-r border-gray-100 shadow-sm z-40 flex flex-col transition-transform duration-300 lg:translate-x-0 lg:static lg:z-auto ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
  <div className="h-14 flex items-center px-5 border-b border-gray-100">
  <img src="/doorly_logo_black.png" alt="Doorly" style={{ height: '32px', width: 'auto', display: 'block', objectFit: 'contain' }} />
  </div>
  <nav className="flex-1 py-3 overflow-y-auto">
  {NAV.map(item => (
  <button key={item.id}
  onClick={() => { setSection(item.id); setSidebarOpen(false); }}
  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${section === item.id ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}>
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
  </svg>
  {item.label}
  </button>
  ))}
  </nav>
  <div className="p-4 border-t border-gray-100">
  <button onClick={logout} className="w-full flex items-center gap-2 text-sm text-gray-500 hover:text-red-600 transition-colors px-1 py-1">
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
  Sign Out
  </button>
  </div>
  </aside>

  {/* Main content */}
  <div className="flex-1 flex flex-col min-w-0">
  {/* Top bar */}
  <header className="h-14 bg-white border-b border-gray-100 shadow-sm flex items-center px-4 gap-3 sticky top-0 z-20">
  <button onClick={() => setSidebarOpen(o => !o)} className="lg:hidden text-gray-500 hover:text-gray-800 p-1">
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
  </button>
  <h1 className="font-semibold text-gray-900 text-sm flex-1">{NAV.find(n => n.id === section)?.label}</h1>
  <button onClick={() => setRefreshKey(k => k + 1)} title="Refresh" className="text-gray-400 hover:text-gray-700 p-1">
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
  </button>
  {adminUser && (
  <button onClick={() => { setSection('settings'); setSidebarOpen(false); }}
  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">
  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: BLUE }}>
  {(adminUser.first_name || adminUser.email || 'A')[0].toUpperCase()}
  </div>
  <span className="hidden sm:inline text-xs font-medium">{adminUser.first_name || adminUser.email}</span>
  </button>
  )}
  </header>

  {/* Section content */}
  <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
  {section === 'overview' && <OverviewSection {...sectionProps} />}
  {section === 'leads' && <LeadsSection {...sectionProps} />}
  {section === 'applications' && <ApplicationsSection {...sectionProps} />}
  {section === 'pipeline' && <PipelineSection {...sectionProps} />}
  {section === 'waitlist' && <WaitlistSection {...sectionProps} />}
  {section === 'users' && <UsersSection {...sectionProps} currentUserId={adminUser?.id} />}
  {section === 'documents' && <DocumentsSection {...sectionProps} />}
  {section === 'settings' && <SettingsSection {...sectionProps} />}
  {section === 'homes' && <HomesSection {...sectionProps} />}
  {section === 'option_period' && <OptionPeriodSection {...sectionProps} />}
  {section === 'closing_pipeline' && <ClosingPipelineSection {...sectionProps} />}
  </main>
  </div>

  {/* Force password change modal */}
  {changePwOpen && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
  <h2 className="text-xl font-bold text-gray-900 mb-1">Change Your Password</h2>
  <p className="text-sm text-gray-500 mb-6">Your account requires a password change before continuing.</p>
  {pwError && <div className="mb-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{pwError}</div>}
  <form onSubmit={submitChangePw} className="space-y-4">
  <div>
  <label className="block text-xs font-medium text-gray-500 mb-1">Current Password</label>
  <input type="password" required value={pwForm.current} onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))}
  className="w-full h-11 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
  </div>
  <div>
  <label className="block text-xs font-medium text-gray-500 mb-1">New Password</label>
  <input type="password" required value={pwForm.next} onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))}
  className="w-full h-11 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
  </div>
  <div>
  <label className="block text-xs font-medium text-gray-500 mb-1">Confirm New Password</label>
  <input type="password" required value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
  className="w-full h-11 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
  </div>
  <button type="submit" disabled={pwSaving}
  className="btn-primary btn-full text-white disabled:opacity-50"
  style={{ backgroundColor: BLUE }}>
  {pwSaving ? 'Saving…' : (
  <>
  <span>Set New Password</span>
  <span className="btn-arrow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg></span>
  </>
  )}
  </button>
  </form>
  </div>
  </div>
  )}
  </div>
  );
}
